import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';
import { AddToScheduleWindow, canAddToSchedule } from './AddToSchedule';
import WeekOffWindow from './WeekOffWindow';
import { WO_COLUMNS } from './WorkOrdersBoard';
import { BidsTable, estimatorOptionsFor } from './AdminBids';
import { WorkflowCards } from './WorkflowSchedule';
import { SHOP_ORDER_META } from '../../shopOrderTemplates';

// ─────────────────────────────────────────────────────────────────────────────
// Big Picture — when every job is happening, and whether anyone is on it.
//
// Two views of the same data. KANBAN is for moving work along: cards in columns by
// status, drag one across to change it. LIST is for putting the data in: every job
// on a row, dates you can tab through, headings you can sort by.
//
// One card per job carrying both lines, dirt on top and electrical below, and a
// switch that decides which line the columns and the colours follow.
//
// The thing that makes it worth looking at: a date going by does NOT mean anyone is
// on the job. The board asks the time clock who is actually out there — clocked in
// right now, or hours booked over the window — and colours on the answer. Past its
// date with a crew on it is fine. The same job with nobody on it is the one you
// needed to see.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'not_ready', label: 'Not ready',   tint: '#94a3b8' },
  { key: 'ready',     label: 'Ready now',   tint: '#16a34a' },
  { key: 'filler',    label: 'Filler work', tint: '#a16207' },
  { key: 'working',   label: 'Working now', tint: '#1d4ed8' },
  { key: 'complete',  label: 'Complete',    tint: '#0f766e' },
];
const COL_BY_KEY = Object.fromEntries(COLUMNS.map(c => [c.key, c]));

const LINES = [
  { key: 'dirt', label: 'Dirt', tint: '#a16207' },
  { key: 'elec', label: 'Electrical', tint: '#1d4ed8' },
];

// Yellow starts here — far enough out to do something about it.
const WARN_DAYS = 5;

const ymd = d => d.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
const shortDate = s => (s ? new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const daysBetween = (a, b) => Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000);

// Getting a first pass of dates in shouldn't mean opening a calendar 160 times. Two
// shortcuts: pick the MONTH something starts in — the 1st of it, as a baseline you
// can sharpen later — and give the length in weeks instead of hunting for the finish.
function nextMonths(n = 8) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    });
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}
const MONTHS = nextMonths();

// Whole weeks read back into the box; anything else leaves it blank rather than
// rounding, because a shown "2" that really means 11 days would be a lie.
function weeksOf(plan, line) {
  const d = plan?.[`${line}_days`];
  if (!d || d % 7 !== 0) return '';
  return d / 7;
}

// Green means someone is on it — either finished, or a crew is genuinely out there.
// Red means a date has gone by and nobody is. That distinction is the whole board.
function lineTone(plan, line, today, evidence) {
  const start = plan?.[`${line}_date`], stop = plan?.[`${line}_stop`];
  if ((plan?.[`${line}_state`] || '') === 'complete') {
    return { key: 'done', bg: '#ecfdf5', fg: '#0f766e', border: '#99f6e4', word: 'Complete' };
  }
  if (!start && !stop) return { key: 'none', bg: 'transparent', fg: '#9ca3af', border: '#e5e7eb', word: '' };

  if (evidence.working) {
    return { key: 'working', bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0',
      word: evidence.onClock.length
        ? `On the job now · ${evidence.onClock.join(', ')}`
        : `Worked ${shortDate(evidence.lastWorked)} · ${evidence.hours}h` };
  }
  const overdue = (stop && daysBetween(today, stop) < 0) || (!stop && start && daysBetween(today, start) < 0);
  if (overdue) return { key: 'late', bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', word: 'Date has passed — nobody on it' };

  const soon = [start, stop].filter(Boolean).map(d => daysBetween(today, d)).filter(n => n >= 0);
  if (soon.length && Math.min(...soon) <= WARN_DAYS) {
    const n = Math.min(...soon);
    return { key: 'soon', bg: '#fffbeb', fg: '#b45309', border: '#fde68a', word: n === 0 ? 'Today' : `In ${n} day${n === 1 ? '' : 's'}` };
  }
  return { key: 'ok', bg: 'transparent', fg: '#6b7280', border: '#e5e7eb', word: '' };
}

// Where a line stands (Ryan, Oct 30 2026). Complete and Filler work are picked by a person and stay
// exactly as set. Everything else follows the dates: a start still to come (or none) is Not ready;
// once the start comes it is Ready — the date wins, even over a Not ready picked by hand — and it is
// Working now while someone of that line's trade is on today's QuickBooks Time schedule for the job
// (operators for dirt, electricians for electrical; job.schedToday, filled in by the board).
function columnFor(job, line, today) {
  const p = job.plan || {};
  const state = p[`${line}_state`] || 'not_ready';
  if (state === 'complete' || p[`${line}_done`]) return 'complete';
  if (state === 'filler') return 'filler';
  const start = p[`${line}_date`];
  if (!start || start > today) return 'not_ready';
  return job.schedToday && job.schedToday[line] ? 'working' : 'ready';
}

// Columns by date means by the month the work starts in — the grain a schedule is
// actually discussed at. Anything already gone by collects in one bucket ahead of
// them, and anything undated in one behind, so nothing quietly disappears.
const NO_DATE = '~none';
const EARLIER = '!earlier';
function monthKey(job, line, today) {
  const d = job.plan?.[`${line}_date`];
  if (!d) return NO_DATE;
  return d.slice(0, 7) < today.slice(0, 7) ? EARLIER : d.slice(0, 7);
}
const monthLabel = k => (k === NO_DATE ? 'No date yet'
  : k === EARLIER ? 'Earlier'
  : new Date(k + '-01T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));

function evidenceFor(job) {
  const onClock = job.onClock || [];
  return {
    onClock,
    hours: job.recentHours || 0,
    lastWorked: job.lastWorked,
    working: onClock.length > 0 || (job.recentHours || 0) > 0,
  };
}

export default function BigPictureBoard({ onBack }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [line, setLine] = useState('dirt');
  // The Calendar is home base (Ryan, Oct 29 2026); List shows the same things, Cards one board.
  const [view, setView] = useState('calendar');     // calendar | list | kanban
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState('');
  const [drag, setDrag] = useState(null);           // the card being dragged
  const [over, setOver] = useState(null);           // the column under it
  const [colSort, setColSort] = useState('date');             // how cards stack in a column
  const [overCard, setOverCard] = useState(null);             // { id, where } while dragging
  const dragRef = useRef(null);                               // synchronous: state lags a drag
  const [groupBy, setGroupBy] = useState('status');           // what the columns are
  // Jobs with both lines complete fall off the cards (Pat, Sep 2026); this brings them back.
  const [showDone, setShowDone] = useState(false);
  const today = ymd(new Date());
  // What the calendar and list show: the kinds switched on, the PMs ticked (all, to start —
  // untick the ones not wanted), and Month / Week / Day with the day it is looking at.
  const role = calRole();
  const avail = useMemo(() => CAL_KINDS.filter(k => !k.who || k.who.includes(role)), [role]);
  const [kindsOff, setKindsOff] = useState(() => readSet(CAL_KINDS_OFF_KEY));
  const [pmOff, setPmOff] = useState(() => readSet(CAL_PM_OFF_KEY));
  const [period, setPeriodRaw] = useState(() => { try { return ['month', 'week', 'day'].includes(localStorage.getItem(CAL_PERIOD_KEY)) ? localStorage.getItem(CAL_PERIOD_KEY) : 'month'; } catch { return 'month'; } });
  const setPeriod = p => { setPeriodRaw(p); try { localStorage.setItem(CAL_PERIOD_KEY, p); } catch {} };
  const [focus, setFocus] = useState(today);
  // Quick Move (Ryan, Nov 2 2026): a name dragged onto a job on the Week or Day view just goes there —
  // one already on a job moves that one shift, one from the Not-scheduled box is put on for that one
  // day, 7:00–3:30 — with no box and no Schedule window. It was a red on/off button; Ryan (Nov 4 2026):
  // "it works perfect when the button is on — leave as is and remove the button", so it is simply how
  // the board works now.
  const quickMove = true;
  // "Publish Time Off" (Ryan, Nov 4 2026): with the Vacations switch on, the same "Move time off onto the
  // schedule" window as the Vacation page, opened on the week in view. The crews are read again after.
  const [weekOff, setWeekOff] = useState(false);
  const [weekOffNote, setWeekOffNote] = useState('');
  const [crewRefresh, setCrewRefresh] = useState(0);
  useEffect(() => { if (!weekOffNote) return undefined; const t = setTimeout(() => setWeekOffNote(''), 7000); return () => clearTimeout(t); }, [weekOffNote]);
  // Cards shows ONE board at a time (Ryan, Oct 29 2026): Dirt, Electrical or one of the four workflows.
  const CARD_KINDS = ['dirt', 'elec', 'vert', 'horiz', 'loops', 'small'];
  const [cardKind, setCardKindRaw] = useState(() => { try { const v = localStorage.getItem('titan_bp_card_kind'); return CARD_KINDS.includes(v) ? v : 'dirt'; } catch { return 'dirt'; } });
  const setCardKind = k => { setCardKindRaw(k); try { localStorage.setItem('titan_bp_card_kind', k); } catch {} if (k === 'dirt' || k === 'elec') setLine(k); };
  useEffect(() => { if (cardKind === 'dirt' || cardKind === 'elec') setLine(cardKind); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pmShown = useCallback(n => !pmOff.has(pmKey(n)), [pmOff]);
  const toggleIn = (setter, key) => v => setter(s => { const n = new Set(s); if (n.has(v)) n.delete(v); else n.add(v); saveSet(key, n); return n; });
  const toggleKind = toggleIn(setKindsOff, CAL_KINDS_OFF_KEY);
  const togglePm = toggleIn(setPmOff, CAL_PM_OFF_KEY);
  // The workflows' run of dates (drilling, loops and small-job cards), the bids and the time off —
  // each fetched only by someone who may see it.
  const [wfRows, setWfRows] = useState([]);
  const [wfMeta, setWfMeta] = useState({ rigsByType: {}, canEdit: false });   // the drills per kind, and who may change them
  const [bids, setBids] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [employees, setEmployees] = useState([]);   // the estimator boxes in the Bids table
  const [hist, setHist] = useState(null);           // the record of past days: { first_day, days }
  // Office / Field / Shop tabs in the people pillow (Ryan, Oct 31 2026): Office = the PMs; Field = the
  // field supervisors; Shop = the shop crew. Untick someone and their work drops off the calendar.
  const [shopOrders, setShopOrders] = useState([]);
  const [people, setPeople] = useState({ field: [], shop: [], crew: [] });
  const [peopleTab, setPeopleTabRaw] = useState(() => { try { const v = localStorage.getItem('titan_bp_people_tab'); return ['office', 'field', 'shop'].includes(v) ? v : 'office'; } catch { return 'office'; } });
  const setPeopleTab = t => { setPeopleTabRaw(t); try { localStorage.setItem('titan_bp_people_tab', t); } catch {} };
  // The Field tab (Ryan, Oct 31 2026): boxes by trade — Field Supervisors, Operator, Laborer… — not by person.
  const [tradeOff, setTradeOff] = useState(() => readSet(CAL_FIELD_TRADES_OFF_KEY));
  const [shopOff, setShopOff] = useState(() => readSet(CAL_SHOP_OFF_KEY));
  const toggleTrade = toggleIn(setTradeOff, CAL_FIELD_TRADES_OFF_KEY);
  const toggleShop = toggleIn(setShopOff, CAL_SHOP_OFF_KEY);
  const fieldMode = peopleTab === 'field';
  const narrow = useNarrow();
  // The finish and the drill typed straight into the List (Ryan, Oct 31 2026) — the same save as the
  // Scheduled Workflows table; the row comes back worked out (finish → working days, double-booking).
  const saveWf = async (e, body) => {
    const r = e.card || e.wo;
    if (!r) return;
    try {
      const row = await apiFetch(`/workflow-schedule/${e.card ? 'card' : 'wo'}/${r.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (row && row.key) setWfRows(rows => rows.map(x => (x.key === row.key ? row : x)));
      // The other cards on that drill can be double-booked now too — read them all again.
      apiFetch('/workflow-schedule').then(d => { if (Array.isArray(d?.rows)) setWfRows(d.rows); }).catch(() => {});
    } catch (err) { window.alert(err?.error || 'That did not save.'); }
  };
  useEffect(() => { apiFetch('/calendar/history').then(setHist).catch(() => setHist(null)); }, []);
  useEffect(() => {
    apiFetch('/workflow-schedule').then(d => { setWfRows(Array.isArray(d?.rows) ? d.rows : []); setWfMeta({ rigsByType: d?.rigs_by_type || {}, canEdit: !!d?.can_edit }); }).catch(() => setWfRows([]));
    if (avail.some(k => k.key === 'bids')) {
      apiFetch('/bids').then(d => setBids(Array.isArray(d) ? d : [])).catch(() => setBids([]));
      apiFetch('/users').then(d => setEmployees(Array.isArray(d) ? d : [])).catch(() => setEmployees([]));
    }
    if (avail.some(k => k.key === 'off')) apiFetch('/time-off').then(d => setTimeOff(Array.isArray(d) ? d : [])).catch(() => setTimeOff([]));
    apiFetch('/shop-orders').then(d => setShopOrders(Array.isArray(d) ? d : [])).catch(() => setShopOrders([]));
    apiFetch('/big-picture/people').then(d => setPeople({ field: Array.isArray(d?.field) ? d.field : [], shop: Array.isArray(d?.shop) ? d.shop : [], crew: Array.isArray(d?.crew) ? d.crew : [] })).catch(() => {});
  }, [avail]);

  // The dates draw the board; the time clock only fills in who is standing on each
  // job. Waiting on QuickBooks for the second thing held up the first by seconds
  // every time, so the two are fetched apart and the board appears on the fast one.
  const load = useCallback(() => {
    apiFetch('/big-picture/plans?days=14')
      .then(d => {
        setData(d);
        apiFetch('/big-picture/crew?days=14')
          .then(c => setData(prev => prev && ({ ...prev, days: c.days, timeClock: c.timeClock,
            jobs: prev.jobs.map(j => ({ ...j, ...(c.byJob[j.id] || {}) })) })))
          // The board stays up if the clock never answers; the cards just say so.
          .catch(() => setData(prev => prev && ({ ...prev, timeClock: 'unavailable' })));
      })
      .catch(e => setErr(e?.error || 'Could not load the board.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  // A job put on the calendar by hand for some days, or one of those days taken off (Ryan, Oct 31 2026).
  const addExtraDays = (projectId, line, days) => apiFetch('/big-picture/extra-days', { method: 'POST', body: JSON.stringify({ projectId, line, days }) })
    .then(load).catch(e => window.alert(e?.error || 'The job did not go on the calendar.'));
  const removeExtraDay = (e, day) => apiFetch(`/big-picture/extra-days?projectId=${encodeURIComponent(e.projectId)}&line=${e.kind}&day=${day}`, { method: 'DELETE' })
    .then(load).catch(err => window.alert(err?.error || 'That did not come off.'));

  // Every active job gets a card, planned or not. A job with no dates on it sits in
  // Not ready with empty boxes — that column is the list of jobs still waiting for
  // someone to say when they happen, which is the reason to open this at all.
  // Today's QuickBooks Time schedule, so a line reads Working now while its own trade is on the job.
  const [crewToday, setCrewToday] = useState(null);
  useEffect(() => { apiFetch(`/big-picture/day-crew?date=${today}`).then(setCrewToday).catch(() => setCrewToday(null)); }, [today]);
  const jobsNow = useMemo(() => (data ? data.jobs.map(j => {
    const people = (crewToday && crewToday.byJob && crewToday.byJob[j.id] && crewToday.byJob[j.id].people) || [];
    const has = kind => jobCrewOnly(people).some(x => LINE_TRADES[kind].includes(x.trade));
    return { ...j, schedToday: { dirt: has('dirt'), elec: has('elec') } };
  }) : []), [data, crewToday]);
  const cards = useMemo(() => {
    const q = search.trim().toLowerCase();
    // A job with BOTH lines complete is finished as far as this board goes — it falls off.
    const all = data ? jobsNow.filter(j => (showDone || !(j.plan && j.plan.dirt_done && j.plan.elec_done)) && !pmOff.has(pmKey(j.pm_name))) : [];
    return q ? all.filter(j => `${j.project_number} ${j.name} ${j.pm_name}`.toLowerCase().includes(q)) : all;
  }, [data, jobsNow, search, showDone, pmOff]);

  // Every PM anything on the page belongs to — a box each.
  const allPms = useMemo(() => {
    const names = new Set();
    for (const j of (data ? data.jobs : [])) names.add(pmKey(j.pm_name));
    for (const r of wfRows) names.add(pmKey(r.pm_name));
    for (const b of bids) if (BID_OPEN.includes(b.status)) names.add(pmKey(b.bid_pm_name));
    return [...names].sort((a, b) => (a === NO_PM) - (b === NO_PM) || a.localeCompare(b));
  }, [data, wfRows, bids]);

  // Everything for the calendar and the list, narrowed by the switches, the PM boxes and the search.
  // On the Field tab bids don't show (Ryan, Oct 31 2026).
  const shownKinds = useMemo(() => avail.filter(k => !kindsOff.has(k.key) && !(fieldMode && k.key === 'bids')), [avail, kindsOff, fieldMode]);
  const entries = useMemo(() => {
    if (!data) return [];
    const on = new Set(shownKinds.map(k => k.key));
    const q = search.trim().toLowerCase();
    const past = [];
    for (const [day, list] of Object.entries((hist && hist.days) || {})) for (const e of list) past.push({ ...e, key: `${e.key}@${day}`, onDay: day });
    return calEntries(data.jobs, wfRows, bids, timeOff, shopOrders).concat(past).filter(e => on.has(e.kind)
      && (e.noPm || fieldMode || !pmOff.has(pmKey(e.pm)))   // every PM on the Field tab
      && !(e.shop && e.shop.assignee_id && shopOff.has(e.shop.assignee_id))
      && (!q || `${e.number || ''} ${e.name || ''} ${e.pm || ''}`.toLowerCase().includes(q)));
  }, [data, wfRows, bids, timeOff, shopOrders, hist, shownKinds, pmOff, shopOff, search, fieldMode]);
  // Every tile, whatever is switched on or ticked — "not scheduled" means not on ANY tile that day.
  const allEntries = useMemo(() => {
    if (!data) return [];
    const past = [];
    for (const [day, list] of Object.entries((hist && hist.days) || {})) for (const e of list) past.push({ ...e, key: `${e.key}@${day}`, onDay: day });
    return calEntries(data.jobs, wfRows, [], []).concat(past);
  }, [data, wfRows, hist]);
  const cut = useMemo(() => (hist && hist.days ? { days: new Set(Object.keys(hist.days)) } : null), [hist]);
  const win = periodWindow(period, focus, today);
  // The List's lower parts, in the Month / Week / Day picked: the bids in the Bids page's own table
  // (Ryan, Oct 29 2026), then the workflows and time off.
  const inWin = e => e.from <= win.to && (e.to || e.from) >= win.from;
  const listEntries = useMemo(() => entries.filter(e => !e.onDay && !['dirt', 'elec', 'bids'].includes(e.kind) && inWin(e)), [entries, win.from, win.to]); // eslint-disable-line react-hooks/exhaustive-deps
  // Upcoming bids only — still being worked on, not yet submitted, the Bids page's Upcoming tab (Ryan, Oct 29 2026).
  const bidRows = useMemo(() => entries.filter(e => e.kind === 'bids' && e.bid.status === 'draft' && inWin(e)).map(e => e.bid), [entries, win.from, win.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const estimators = useMemo(() => estimatorOptionsFor(employees, bids), [employees, bids]);

  // The List's top part — the Large Jobs table, Dirt and Electrical on one line per job, typed
  // into (Ryan, Oct 29 2026: "I really liked how that was working"). Every job, dated or not,
  // narrowed by the PM boxes and the search like everything else. It sorts on whichever heading was clicked.
  const [sort, setSort] = useState({ key: 'job', dir: 1 });
  const rows = useMemo(() => {
    const rank = j => {
      switch (sort.key) {
        case 'pm':          return (j.pm_name || '').toLowerCase();
        case 'dirt_state':  return String(COLUMNS.findIndex(c => c.key === columnFor(j, 'dirt', today)));
        case 'elec_state':  return String(COLUMNS.findIndex(c => c.key === columnFor(j, 'elec', today)));
        case 'crew':        return String(1e6 - (j.recentHours || 0)).padStart(9, '0');
        case 'job':         return String(j.project_number || '');
        default:            return j.plan?.[sort.key] || '9999-12-31';   // any date column
      }
    };
    return [...cards].sort((a, b) => {
      const x = rank(a), y = rank(b);
      if (x === y) return String(a.project_number).localeCompare(String(b.project_number));
      return (x < y ? -1 : 1) * sort.dir;
    });
  }, [cards, sort, today]);
  const clickSort = key => setSort(s => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const finishedCount = useMemo(() => (data ? data.jobs.filter(j => j.plan && j.plan.dirt_done && j.plan.elec_done).length : 0), [data]);

  const pmOptions = useMemo(
    () => [...new Set(cards.map(j => j.pm_name).filter(Boolean))].sort(),
    [cards]);

  // The columns are the statuses, or they're the PMs. Same cards either way — one
  // answers "where does the work stand", the other "what is each PM carrying".
  // Finished work gets a column of its own on the PM and month boards too, rather
  // than vanishing — it's marked complete on the status board, and this is where it
  // lands afterwards.
  const DONE_COL = '__complete';
  const boardCards = cards;


  const columns = useMemo(() => {
    const done = { key: DONE_COL, label: 'Complete', tint: '#0f766e' };
    if (groupBy === 'pm') {
      return [...pmOptions]
        .sort((a, b) => (a === 'Unassigned' ? 1 : 0) - (b === 'Unassigned' ? 1 : 0) || a.localeCompare(b))
        .map(nm => ({ key: nm, label: nm, tint: '#1e3a5f' }))
        .concat(done);
    }
    if (groupBy === 'date') {
      const keys = [...new Set(boardCards.filter(j => columnFor(j, line, today) !== 'complete')
        .map(j => monthKey(j, line, today)))]
        .sort((a, b) => (a === EARLIER ? -1 : b === EARLIER ? 1 : 0) || (a === NO_DATE ? 1 : b === NO_DATE ? -1 : a.localeCompare(b)));
      return keys.map(k => ({ key: k, label: monthLabel(k),
        tint: k === EARLIER ? '#b91c1c' : k === NO_DATE ? '#94a3b8' : '#1d4ed8' })).concat(done);
    }
    return COLUMNS;
  }, [groupBy, pmOptions, boardCards, line, today]);

  const byColumn = useMemo(() => {
    const out = Object.fromEntries(columns.map(c => [c.key, []]));
    for (const j of boardCards) {
      const state = columnFor(j, line, today);
      const k = groupBy === 'status' ? state
        : state === 'complete' ? DONE_COL
        : groupBy === 'pm' ? j.pm_name : monthKey(j, line, today);
      (out[k] || out[columns[0]?.key] || []).push(j);
    }
    // Sorting by status inside a column can only mean the OTHER line when the columns
    // ARE the statuses; grouped by PM it means the line you're following.
    const other = line === 'dirt' ? 'elec' : 'dirt';
    const statusLine = groupBy === 'status' ? other : line;
    const rank = j => {
      if (colSort === 'manual') {
        const o = j.plan?.board_order;
        // Never dragged sinks below everything placed by hand.
        return String(o == null ? 99999 : o).padStart(5, '0');
      }
      if (colSort === 'pm')     return j.pm_name.toLowerCase();
      if (colSort === 'job')    return String(j.project_number || '');
      if (colSort === 'status') return String(COLUMNS.findIndex(c => c.key === columnFor(j, statusLine, today)));
      return j.plan?.[`${line}_date`] || '9999-12-31';        // undated sinks to the bottom
    };
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => {
        const x = rank(a), y = rank(b);
        if (x === y) return String(a.project_number).localeCompare(String(b.project_number));
        return x < y ? -1 : 1;
      });
    }
    return out;
  }, [boardCards, line, today, colSort, groupBy, columns]);

  // target null means the end of the column. Placing a card by hand is only meaningful
  // against a hand order, so it turns that on rather than being silently undone by the
  // sort on the next render.
  async function placeCard(target, colKey, where) {
    const id = dragRef.current;
    dragRef.current = null;
    setDrag(null); setOver(null); setOverCard(null);
    if (!id || (target && id === target.id)) return;
    const dragged = boardCards.find(x => x.id === id);
    if (!dragged) return;
    if (groupBy === 'status') await setLineState(dragged, line, colKey);
    const column = (byColumn[colKey] || []).filter(x => x.id !== id);
    let at = target ? column.findIndex(x => x.id === target.id) : -1;
    if (at < 0) at = column.length;
    else if (where === 'after') at += 1;
    const ids = [...column.slice(0, at), dragged, ...column.slice(at)].map(x => x.id);
    setColSort('manual');
    setData(d => d && ({ ...d, jobs: d.jobs.map(j => {
      const i = ids.indexOf(j.id);
      return i < 0 ? j : { ...j, plan: { ...(j.plan || {}), board_order: i } };
    }) }));
    apiFetch('/big-picture/board-order', { method: 'POST', body: JSON.stringify({ order: ids }) }).catch(() => {});
  }

  async function setLineState(job, whichLine, value) {
    return save(job, { [`${whichLine}_state`]: value });
  }
  async function setDate(job, field, value) {
    return save(job, { [field]: value === '' ? null : value });
  }
  async function save(job, patch) {
    setBusy(job.id);
    try {
      const r = await apiFetch(`/projects/${job.id}/big-picture`, { method: 'PUT', body: JSON.stringify(patch) });
      setData(d => ({ ...d, jobs: d.jobs.map(j => (j.id === job.id ? { ...j, plan: r.plan } : j)) }));
    } catch (e) {
      setErr(e?.error || 'Could not save that.');
      load();
    }
    setBusy('');
  }

  if (err && !data) return <div style={{ padding: 30, color: '#b91c1c' }}>{err}</div>;
  // The Dirt & Electrical | Scheduled Workflows switch came off (Ryan, Oct 29 2026): the workflows
  // are on the calendar now, and each board's 📅 Schedule pill still opens its own list.

  if (!data) return <div style={{ padding: 30, color: '#6b7280' }}>Loading the board…</div>;

  const sel = { fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' };
  const openEntry = e => { const to = entryPath(e); if (to) navigate(to); };
  const allPmsOn = allPms.every(n => !pmOff.has(n));
  const setAllPms = on => { const n = on ? new Set() : new Set(allPms); saveSet(CAL_PM_OFF_KEY, n); setPmOff(n); };
  const step = dir => setFocus(f => addDaysYmd(f, dir * (period === 'week' ? 7 : 1)));
  // The header's three upright stacks — Field / Office / Shop, Calendar / List / Cards, Month / Week /
  // Day — all one look (Ryan, Nov 1 2026): the one you are on is red and a touch bigger, the rest white.
  // On a phone the stacks lie flat: one row each, dividers between instead of above.
  const stackBtn = (on, label, onClick, { i = 0, disabled = false, title = '' } = {}) => (
    <button key={label} type="button" onClick={onClick} disabled={disabled} title={title}
      style={{ flex: narrow ? '1 1 auto' : 1, width: narrow ? 'auto' : '100%', minWidth: 0, padding: narrow ? '10px 8px' : '0 12px', border: 'none', cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
        ...(narrow ? { borderLeft: i ? '1px solid #d1d5db' : 'none' } : { borderTop: i ? '1px solid #d1d5db' : 'none' }),
        fontWeight: 800, fontSize: on && !disabled ? (narrow ? 15 : 17) : (narrow ? 13.5 : 15), background: on && !disabled ? '#c2410c' : 'var(--surface)', color: on && !disabled ? '#fff' : disabled ? '#c4c4c4' : 'var(--text)' }}>
      {label}
    </button>
  );
  const stackBox = (width, children) => (
    <div style={{ width: narrow ? '100%' : width, flexShrink: 0, display: 'flex', flexDirection: narrow ? 'row' : 'column', border: '2px solid #d1d5db', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>{children}</div>
  );
  const viewBtn = (k, label, i) => stackBtn(view === k, label, () => setView(k), { i });
  const countLine = (
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
      {view === 'calendar' ? `${entries.length} scheduled` : view === 'list' ? `${rows.length} large jobs · ${bidRows.length} bids · ${listEntries.length} more` : `${boardCards.length} job${boardCards.length === 1 ? '' : 's'}`} ·{' '}
      {data.timeClock === 'ok' ? `crew from the time clock, last ${data.days} days`
        : data.timeClock === 'pending' ? 'checking the time clock…' : 'QuickBooks Time didn’t answer — cards can’t show who’s on the job'}
    </span>
  );
  const searchBox = (
    <input type="text" placeholder="Search job, name or PM…" value={search} onChange={e => setSearch(e.target.value)}
      style={{ ...sel, cursor: 'text', fontSize: 15, padding: '9px 14px', borderRadius: 10, border: '1.5px solid #cbd5e1', background: '#fff', minWidth: narrow ? 0 : 220, flex: narrow ? '1 1 100%' : '0 1 340px' }} />
  );
  const big = { ...sel, fontSize: 15, padding: '9px 16px', borderRadius: 10, border: '1.5px solid #cbd5e1', background: '#fff' };
  const periodBtn = (k, label, i) => {
    const dim = view === 'kanban';
    return stackBtn(period === k, label, () => { setPeriod(k); if (k !== 'month') setFocus(f => f || today); },
      { i, disabled: dim, title: dim ? 'Month, Week and Day are for the Calendar and the List' : `Show the ${label.toLowerCase()}` });
  };

  return (
    <div>
      <button onClick={onBack} style={{ ...sel, fontWeight: 700, marginBottom: 10 }}>‹ Schedule</button>
      {/* Home base (Ryan, Nov 1 2026): Field / Office / Shop upright on the left, the people in the
          middle, then Calendar / List / Cards and Month / Week / Day upright on the right. */}
      <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: 'stretch', gap: narrow ? 10 : 16, marginBottom: 12, minHeight: narrow ? 0 : 150 }}>
        {/* The people pillow fills the middle (Ryan, Oct 31 2026); Field / Office / Shop are its left
            column, inside the same border, so the two read as one thing (Ryan, Nov 1 2026). On a phone
            the column is the pillow's top row. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: 'stretch', border: '2px solid #d1d5db', borderRadius: 16, background: '#fff', overflow: 'hidden' }}>
        <div style={{ ...(narrow ? { width: '100%', flexDirection: 'row', borderBottom: '2px solid #d1d5db' } : { width: 130, flexDirection: 'column', borderRight: '2px solid #d1d5db' }), flexShrink: 0, display: 'flex' }}>
          {[['field', 'Field'], ['office', 'Office'], ['shop', 'Shop']].map(([t, label], i) => stackBtn(peopleTab === t, label, () => setPeopleTab(t), { i }))}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', background: '#f1f5f9', borderBottom: '2px solid #d1d5db', minHeight: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', flexWrap: 'wrap', minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{peopleTab === 'field' ? 'Field crew by trade' : peopleTab === 'shop' ? 'Shop Crew' : 'Project Managers'}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{peopleTab === 'field' ? 'every job, every PM — untick a trade to hide those people' : `untick the ones you don't want to see${peopleTab === 'shop' ? ' — their shop orders drop off' : ''}`}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px', flex: 1 }}>
          {peopleTab === 'field' ? (
            <TradeChips crew={people.crew} off={tradeOff} onToggle={toggleTrade}
              onAll={on => { const n = on ? new Set() : new Set(FIELD_GROUPS.map(g => g.key)); saveSet(CAL_FIELD_TRADES_OFF_KEY, n); setTradeOff(n); }} />
          ) : peopleTab === 'shop' ? (
            <PeopleChips list={people.shop} off={shopOff} onToggle={toggleShop}
              onAll={on => { const n = on ? new Set() : new Set(people.shop.map(p => p.userId)); saveSet(CAL_SHOP_OFF_KEY, n); setShopOff(n); }} />
          ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'flex-start', flex: 1 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 800,
              border: '1.5px solid #1e3a5f', background: allPmsOn ? '#1e3a5f' : 'var(--surface)', color: allPmsOn ? '#fff' : '#1e3a5f' }}>
              <input type="checkbox" checked={allPmsOn} onChange={e => setAllPms(e.target.checked)} style={{ margin: 0 }} /> All
            </label>
            {allPms.map(n => {
              const on = !pmOff.has(n);
              return (
                <label key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 700,
                  border: `1.5px solid ${on ? '#93c5fd' : 'var(--border)'}`, background: on ? '#eff6ff' : 'var(--surface)', color: on ? '#1e3a5f' : '#9ca3af' }}>
                  <input type="checkbox" checked={on} onChange={() => togglePm(n)} style={{ margin: 0 }} /> {n}
                </label>
              );
            })}
          </div>
          )}
          {countLine}
          </div>
        </div>
        </div>

        {stackBox(150, [viewBtn('calendar', '📅 Calendar', 0), viewBtn('list', 'List', 1), viewBtn('kanban', 'Cards', 2)])}
        {stackBox(120, [periodBtn('month', 'Month', 0), periodBtn('week', 'Week', 1), periodBtn('day', 'Day', 2)])}
      </div>

      {view !== 'kanban' && (<>
        {/* The switches: one per kind — tick any or all. Each is as wide as its words need, sharing the
            rest of the row evenly, so "Horizontal Drilling" never spills out of its switch (Ryan, Nov 1 2026). */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {avail.filter(k => !(fieldMode && k.key === 'bids')).map(k => {
            const on = !kindsOff.has(k.key);
            return (
              <button key={k.key} type="button" onClick={() => toggleKind(k.key)} aria-pressed={on} title={on ? `Hide ${k.label}` : `Show ${k.label}`}
                style={{ flex: '1 1 auto', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `2px solid ${k.color}`, background: on ? k.color : '#fff', color: on ? k.onColor : k.ink, fontSize: 12, fontWeight: 800 }}>
                <span style={{ flexShrink: 0, width: 13, height: 13, borderRadius: 4, border: `2px solid ${on ? k.onColor : k.color}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, lineHeight: 1 }}>{on ? '✓' : ''}</span>
                {k.label}
              </button>
            );
          })}
        </div>
        {period === 'month'
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
              {searchBox}
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {view === 'calendar' ? 'How many want a crew each working day, across the whole run of the work. Click a number for the list, a day for that day.' : 'Everything from this month on. Click a heading to sort, a row to open it.'}
              </span>
            </div>
          : <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <button onClick={() => step(-1)} style={{ ...big, fontWeight: 800 }}>‹</button>
              {/* On a phone the date is its own line, then ‹ › Today, then the search box full width. */}
              <span style={{ fontSize: narrow ? 16 : 18, fontWeight: 800, minWidth: narrow ? 0 : 220, ...(narrow ? { flex: '1 1 100%', order: -1, textAlign: 'left' } : { textAlign: 'center' }) }}>{periodLabel(period, focus)}</span>
              <button onClick={() => step(1)} style={{ ...big, fontWeight: 800 }}>›</button>
              <button onClick={() => setFocus(today)} style={{ ...big, fontWeight: 700 }}>Today</button>
              {searchBox}
              {view === 'calendar' && canAddToSchedule() && !kindsOff.has('off') && (
                <button type="button" onClick={() => setWeekOff(true)}
                  title="Everyone off the week in view goes from their job to You Are Off (published) and their Sunday note comes down — you see the list first"
                  style={{ ...big, fontWeight: 800, marginLeft: narrow ? 0 : 'auto', flex: narrow ? '1 1 100%' : undefined, border: '2px solid #00695c', background: '#26A69A', color: '#fff' }}>
                  🌴 Publish Time Off
                </button>
              )}
            </div>}
      {weekOff && <WeekOffWindow initialSunday={weekStartOf(focus)} onClose={() => setWeekOff(false)}
        onDone={t => { setWeekOff(false); setWeekOffNote(t); setCrewRefresh(n => n + 1); }} />}
      {weekOffNote && <div style={{ position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 5000, background: '#166534', color: '#fff', fontSize: 14, fontWeight: 700, padding: '10px 18px', borderRadius: 999, boxShadow: '0 6px 20px rgba(0,0,0,0.25)', maxWidth: '90vw' }}>✓ {weekOffNote}</div>}
      </>)}

      {view === 'kanban' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          {CARD_KINDS.map(key => {
            const k = CAL_KIND[key], on = cardKind === key;
            return (
              <button key={key} type="button" onClick={() => setCardKind(key)} aria-pressed={on} title={`Show the ${k.label} cards`}
                style={{ width: 168, boxSizing: 'border-box', padding: '8px 8px', borderRadius: 9, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `2px solid ${k.color}`, background: on ? k.color : '#fff', color: on ? k.onColor : k.ink, fontSize: 13, fontWeight: 800 }}>
                {on ? '● ' : ''}{k.label}
              </button>
            );
          })}
          {searchBox}
        </div>
      )}
      {view === 'kanban' && (cardKind === 'dirt' || cardKind === 'elec') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Columns</span>
            <Segmented tint="#1e3a5f" value={groupBy} onPick={setGroupBy}
              options={[{ k: 'status', label: 'Status' }, { k: 'pm', label: 'PM' }, { k: 'date', label: 'Month' }]} />
          </div>
          <select value={colSort} onChange={e => setColSort(e.target.value)} style={sel} title="How the cards stack inside each column">
            <option value="manual">In the order I put them</option>
            <option value="date">Sort by date</option>
            <option value="pm">Sort by PM</option>
            <option value="status">Sort by {line === 'dirt' ? 'electrical' : 'dirt'} status</option>
            <option value="job">Sort by job number</option>
          </select>
          {finishedCount > 0 && (
            <label title="Jobs with both dirt and electrical marked complete drop off this board"
              style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Show finished jobs ({finishedCount})
            </label>
          )}
        </div>
      )}
      {view === 'kanban' && (cardKind === 'dirt' || cardKind === 'elec') && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
          {groupBy === 'status'
            ? <>Drag a card to a column to set where the {line === 'dirt' ? 'dirt' : 'electrical'} stands. Green means someone is on it.</>
            : <>Columns are {groupBy === 'pm' ? 'the PMs' : 'the month the work starts'}, cards in date order. Set the status on the card — dragging only moves a job between statuses, so it's off here.</>}
        </div>
      )}

      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{err}</div>}

      {view === 'kanban' && !(cardKind === 'dirt' || cardKind === 'elec') ? (
        <WorkflowCards key={cardKind} type={CAL_KIND[cardKind].type || 'small_jobs'} pmShown={pmShown} search={search} />
      ) : view === 'kanban' ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(210px, 1fr))`, gap: 10, alignItems: 'start', overflowX: 'auto' }}>
          {columns.map(c => (
            <div key={c.key}
              onDragOver={e => { if (groupBy !== 'status') return; e.preventDefault(); setOver(c.key); if (overCard) setOverCard(null); }}
              onDragLeave={() => setOver(o => (o === c.key ? null : o))}
              onDrop={e => { if (groupBy !== 'status') return; e.preventDefault(); placeCard(null, c.key, 'after'); }}
              style={{ background: over === c.key ? '#fff7ed' : 'var(--surface-2, #f7f7f5)', borderRadius: 12, padding: 8, minHeight: 160,
                border: `1px solid ${over === c.key ? '#fed7aa' : 'transparent'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px 10px' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: c.tint }} />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)' }}>{c.label}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>{byColumn[c.key].length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {byColumn[c.key].map(j => (
                  <JobCard key={j.id} job={j} line={line} today={today} busy={busy === j.id}
                    draggable={groupBy === 'status'}
                    onDragStart={() => { dragRef.current = j.id; setDrag(j.id); }}
                    onDragEnd={() => { dragRef.current = null; setDrag(null); setOverCard(null); }}
                    dropSide={overCard && overCard.id === j.id ? overCard.where : null}
                    onDragOverCard={e => {
                      if (groupBy !== 'status' || !dragRef.current || dragRef.current === j.id) return;
                      e.preventDefault(); e.stopPropagation();
                      const r = e.currentTarget.getBoundingClientRect();
                      const where = e.clientY > r.top + r.height / 2 ? 'after' : 'before';
                      if (!overCard || overCard.id !== j.id || overCard.where !== where) setOverCard({ id: j.id, where });
                    }}
                    onDropCard={e => { e.preventDefault(); e.stopPropagation(); placeCard(j, c.key, overCard && overCard.where); }}
                    onOpen={() => navigate(`/admin/jobs/${j.id}/schedule`)}
                    onDate={(field, v) => setDate(j, field, v)}
                    onState={(l, v) => setLineState(j, l, v)} />
                ))}
                {byColumn[c.key].length === 0 && (
                  <div style={{ fontSize: 11.5, color: '#c4c4c4', padding: '10px 6px' }}>Nothing here.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : view === 'calendar' ? (
        <CalendarView narrow={narrow} entries={entries} cut={cut} kinds={shownKinds} today={today} period={period} focus={focus}
          onFocus={setFocus} onPeriod={setPeriod} onGo={openEntry}
          allEntries={allEntries} fieldPeople={fieldMode ? people.crew : people.field} fieldMode={fieldMode}
          personHidden={p => fieldMode && tradeOff.has(fieldGroupOf(p))} timeOff={timeOff}
          jobs={data ? data.jobs : []} onAddDays={addExtraDays} onRemoveDay={removeExtraDay} quickMove={quickMove} crewRefresh={crewRefresh} />
      ) : (<>
        {/* Large Jobs: Dirt and Electrical on the same line, typed into — shown while either switch is on. */}
        {(!kindsOff.has('dirt') || !kindsOff.has('elec')) && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 800 }}>Large Jobs — <span style={{ color: CAL_KIND.dirt.ink }}>Dirt</span> &amp; <span style={{ color: CAL_KIND.elec.ink }}>Electrical</span></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length} job{rows.length === 1 ? '' : 's'} · click a heading to sort; tab across the boxes — they save as you go</span>
              {finishedCount > 0 && (
                <label title="Jobs with both dirt and electrical marked complete drop off this list"
                  style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 'auto' }}>
                  <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Show finished jobs ({finishedCount})
                </label>
              )}
            </div>
            <JobTable rows={rows} today={today} sort={sort} onSort={clickSort} busy={busy}
              onDate={setDate} onState={setLineState}
              onOpen={j => navigate(`/admin/jobs/${j.id}/schedule`)} />
          </div>
        )}
        {shownKinds.some(k => k.key === 'bids') && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
              <span style={{ color: CAL_KIND.bids.ink }}>Upcoming Bids</span> <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>· due {period === 'month' ? 'from this month on' : `this ${period}`} · {bidRows.length} — the same table as the Bids page; changes save as you go</span>
            </div>
            {bidRows.length
              ? <div className="admin-card" style={{ overflow: 'hidden' }}><BidsTable rows={bidRows} setBids={setBids} estimators={estimators} /></div>
              : <div style={{ fontSize: 13.5, color: '#9ca3af', padding: '10px 4px' }}>No upcoming bids due in this {period === 'month' ? 'year ahead' : period}.</div>}
          </div>
        )}
        {shownKinds.some(k => !['dirt', 'elec', 'bids'].includes(k.key)) && (<>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
            Workflows &amp; Vacations <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>· {period === 'month' ? 'from this month on' : `this ${period}`}</span>
          </div>
          <EverythingList entries={listEntries} today={today} onGo={openEntry} rigsByType={wfMeta.rigsByType} canEdit={wfMeta.canEdit} onSave={saveWf} />
        </>)}
      </>)}
    </div>
  );
}

// ── The schedule home (Ryan, Oct 29 2026) ─────────────────────────────────────
// One calendar for everything — dirt, electrical, the four workflows, bid dates, time off —
// with a List that shows the same things and Cards for one board at a time. "This is the home base."
//
// A job counts on every working day from its start to its finish, not just the day
// it starts. Counting starts would show six jobs beginning on the 14th and hide the
// five already running through it, which is the opposite of the point.
//
// The kinds, each in its own colour — the squares match the switch (his colour sheet, his order).
// color = the topic's colour, ink = its numbers and words (yellow needs a darker gold to be read),
// soft = the square. `who` limits a kind to the people whose data it is: bids are office business,
// time off is private to Executives and the office (Oct 7 2026).
const CAL_KINDS = [
  { key: 'dirt',  label: 'Dirt',                color: '#8B5A2B', ink: '#7a4c20', soft: '#f6eee6', onColor: '#fff' },
  { key: 'elec',  label: 'Electrical',          color: '#F4C430', ink: '#8a6500', soft: '#fff8dc', onColor: '#3d2e00' },
  { key: 'vert',  label: 'Vertical Drilling',   color: '#7B61A8', ink: '#5b4585', soft: '#f1edf7', onColor: '#fff', type: 'vertical_drilling' },
  { key: 'horiz', label: 'Horizontal Drilling', color: '#F28C28', ink: '#a85a0c', soft: '#fef3e7', onColor: '#fff', type: 'horizontal_drilling' },
  { key: 'loops', label: 'Loops',               color: '#3B82F6', ink: '#1d4ed8', soft: '#eef4ff', onColor: '#fff', type: 'loops' },
  { key: 'small', label: 'Small Jobs',          color: '#4CAF50', ink: '#2e7d32', soft: '#edf7ee', onColor: '#fff' },
  { key: 'bids',  label: 'Bids',                color: '#E53935', ink: '#b71c1c', soft: '#fdecec', onColor: '#fff', who: ['admin', 'Admin', 'Executive', 'ProjectManager', 'Accounting', 'OfficeStaff'] },
  { key: 'off',   label: 'Vacations',           color: '#26A69A', ink: '#00695c', soft: '#e6f6f4', onColor: '#fff', who: ['admin', 'Admin', 'Executive', 'Accounting'] },
  // Shop orders on their needed-by day (Ryan, Oct 31 2026).
  { key: 'shop',  label: 'Shop',                color: '#64748B', ink: '#334155', soft: '#f1f5f9', onColor: '#fff' },
];
const CAL_KIND = Object.fromEntries(CAL_KINDS.map(k => [k.key, k]));
// What is remembered is what someone switched OFF / unticked, so a kind or a PM added later starts on.
const CAL_KINDS_OFF_KEY = 'titan_bp_kinds_off';
const CAL_PM_OFF_KEY = 'titan_bp_pm_off';
const CAL_FIELD_TRADES_OFF_KEY = 'titan_bp_field_trades_off';
// The Field tab's boxes (Ryan, Oct 31 2026). Supervisors go by the Supervisors box; everyone else by trade.
const FIELD_GROUPS = [
  { key: 'sup', label: 'Field Supervisors' }, { key: 'Operator', label: 'Operator' }, { key: 'Laborer', label: 'Laborer' },
  { key: 'Electrician', label: 'Electrician' }, { key: 'Operator Apprentice', label: 'Operator Apprentice' }, { key: 'Electrical Apprentice', label: 'Electrical Apprentice' },
];
const fieldGroupOf = p => (p.supervisor ? 'sup' : p.trade || null);
function TradeChips({ crew, off, onToggle, onAll }) {
  const allOn = FIELD_GROUPS.every(g => !off.has(g.key));
  const chip = on => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 700,
    border: `1.5px solid ${on ? '#93c5fd' : 'var(--border)'}`, background: on ? '#eff6ff' : 'var(--surface)', color: on ? '#1e3a5f' : '#9ca3af' });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'flex-start', flex: 1 }}>
      <label style={{ ...chip(true), fontWeight: 800, border: '1.5px solid #1e3a5f', background: allOn ? '#1e3a5f' : 'var(--surface)', color: allOn ? '#fff' : '#1e3a5f' }}>
        <input type="checkbox" checked={allOn} onChange={e => onAll(e.target.checked)} style={{ margin: 0 }} /> All
      </label>
      {FIELD_GROUPS.map(g => {
        const on = !off.has(g.key);
        const n = crew.filter(p => fieldGroupOf(p) === g.key).length;
        return (
          <label key={g.key} style={chip(on)}>
            <input type="checkbox" checked={on} onChange={() => onToggle(g.key)} style={{ margin: 0 }} /> {g.label}
            <span style={{ fontSize: 12, fontWeight: 600, color: on ? '#64748b' : '#cbd5e1' }}>{n}</span>
          </label>
        );
      })}
    </div>
  );
}
const CAL_SHOP_OFF_KEY = 'titan_bp_shop_off';

// The Field / Shop tabs' boxes: All, then one per person — the same look as the PM boxes.
function PeopleChips({ list, off, onToggle, onAll }) {
  const allOn = list.every(p => !off.has(p.userId));
  const chip = on => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 15, fontWeight: 700,
    border: `1.5px solid ${on ? '#93c5fd' : 'var(--border)'}`, background: on ? '#eff6ff' : 'var(--surface)', color: on ? '#1e3a5f' : '#9ca3af' });
  if (!list.length) return <div style={{ fontSize: 13, color: '#9ca3af', flex: 1 }}>Nobody here yet.</div>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'flex-start', flex: 1 }}>
      <label style={{ ...chip(true), fontWeight: 800, border: '1.5px solid #1e3a5f', background: allOn ? '#1e3a5f' : 'var(--surface)', color: allOn ? '#fff' : '#1e3a5f' }}>
        <input type="checkbox" checked={allOn} onChange={e => onAll(e.target.checked)} style={{ margin: 0 }} /> All
      </label>
      {list.map(p => {
        const on = !off.has(p.userId);
        return (
          <label key={p.userId} style={chip(on)}>
            <input type="checkbox" checked={on} onChange={() => onToggle(p.userId)} style={{ margin: 0 }} /> {p.name}
          </label>
        );
      })}
    </div>
  );
}
const CAL_PERIOD_KEY = 'titan_bp_period';
const NO_PM = 'No PM';
// A job with nobody on it says 'Unassigned', a card or bid says nothing — one box for both.
const pmKey = n => (n && n !== 'Unassigned' ? n : NO_PM);
const BID_OPEN = ['potential', 'draft', 'submitted'];
const BID_WORD = { potential: 'Potential', draft: 'Draft', submitted: 'Submitted' };
const OFF_WORD = { vacation: 'Vacation', sick: 'Sick', personal: 'Personal', other: 'Time off' };
const calRole = () => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || null; } catch { return null; } };
const readSet = key => { try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return new Set(Array.isArray(v) ? v : []); } catch { return new Set(); } };
const saveSet = (key, set) => { try { localStorage.setItem(key, JSON.stringify([...set])); } catch {} };
const addDaysYmd = (s, n) => { const d = new Date(s + 'T12:00:00'); d.setDate(d.getDate() + n); return ymd(d); };
const weekStartOf = s => addDaysYmd(s, -new Date(s + 'T12:00:00').getDay());   // Sunday, like the month grid
const isWeekend = s => { const g = new Date(s + 'T12:00:00').getDay(); return g === 0 || g === 6; };

// Every piece of scheduled work as one entry: its kind, its run of dates, what the lists show and
// what opens it. Finished work is not load — it happened — and never comes here.
function calEntries(jobs, wfRows, bids, timeOff, shopOrders = []) {
  const out = [];
  for (const j of jobs) {
    const p = j.plan || {};
    for (const line of ['dirt', 'elec']) {
      if (p[`${line}_done`] || !p[`${line}_date`]) continue;
      out.push({ kind: line, key: `${line}:${j.id}`, projectId: j.id, number: j.project_number, name: j.name, pm: j.pm_name || '',
        from: p[`${line}_date`], to: p[`${line}_stop`] || p[`${line}_date`], workflow: 'job', job: j, filler: p[`${line}_state`] === 'filler' });
    }
    // Days put on the calendar by hand (Ryan, Oct 31 2026) — a finished job's punch list, a dirt hand
    // helping — each its own one-day tile, unless the line is already on the plan that day. Once the
    // day goes by, it's gone; the job's dates and Complete marks were never touched.
    for (const x of j.extra_days || []) {
      const from = p[`${x.line}_date`], to = p[`${x.line}_stop`] || from;
      if (!p[`${x.line}_done`] && from && x.day >= from && x.day <= to) continue;
      out.push({ kind: x.line, key: `${x.line}:${j.id}:x:${x.day}`, projectId: j.id, number: j.project_number, name: j.name, pm: j.pm_name || '',
        from: x.day, to: x.day, workflow: 'job', job: j, extra: true });
    }
  }
  // The workflows' cards (one per card — a job can have several) and the small jobs: start + working
  // days, a start with no length counting its start day only; done ones are left out by the server.
  const kindOfType = Object.fromEntries(CAL_KINDS.filter(k => k.type).map(k => [k.type, k.key]));
  for (const r of wfRows) {
    if (!r.start || r.done) continue;
    const kind = r.kind === 'wo' ? 'small' : kindOfType[r.type];
    if (!kind) continue;
    out.push({ kind, key: `${kind}:${r.id}`, projectId: r.project_id, number: r.job_number, name: r.job_name, pm: r.pm_name || '',
      from: r.start, to: r.end || r.start, workflow: r.kind === 'wo' ? 'small_jobs' : r.type, ...(r.kind === 'wo' ? { wo: r } : { card: r }),
      // A small job marked filler work (Ryan, Nov 1 2026): in the filler pile on its days, crewed by its own trade.
      ...(r.kind === 'wo' && r.filler ? { filler: true, fillerLine: r.filler } : null) });
  }
  // Bid dates: an open bid on the day it is due — the bid's PM is its PM.
  for (const b of bids) {
    if (!BID_OPEN.includes(b.status) || !/^20\d\d-\d\d-\d\d$/.test(String(b.bid_date || ''))) continue;
    out.push({ kind: 'bids', key: `bids:${b.id}`, number: 'Bid', name: b.job_name || b.client_name || 'Untitled bid', pm: b.bid_pm_name || '',
      from: b.bid_date, to: b.bid_date, anyDay: true, bid: b });
  }
  // Approved time off. Nobody's PM, so the PM boxes never hide it.
  for (const t of timeOff) {
    if (t.status !== 'approved' || !t.start_date) continue;
    out.push({ kind: 'off', key: `off:${t.id}`, number: '', name: t.worker_name || 'Someone', pm: '', noPm: true,
      from: t.start_date, to: t.end_date || t.start_date, off: t });
  }
  // Open shop orders on the day they're needed (Ryan, Oct 31 2026). Nobody's PM either.
  for (const o of shopOrders) {
    const day = String(o.needed_by || '').slice(0, 10);
    if (o.work_status === 'complete' || !/^20\d\d-\d\d-\d\d$/.test(day)) continue;
    const t = SHOP_ORDER_META[o.type];
    out.push({ kind: 'shop', key: `shop:${o.id}`, number: o.project_number || (t ? t.icon : ''), name: o.title || (t ? t.label : 'Shop order'), pm: '', noPm: true,
      from: day, to: day, anyDay: true, shop: o });
  }
  return out;
}

// Entries by day — each working day of its run (a bid only on its date, weekend or not).
// The record of past days (Ryan, Oct 30 2026): job work on a day before today, from the day the record
// began, is drawn from what was written down that day (entries with onDay) — not from the dates as
// they stand now. `cut` = { days: the set of days that HAVE a record } — a day the server missed
// falls back to the dates, rather than showing empty.
const JOB_KINDS = ['dirt', 'elec', 'vert', 'horiz', 'loops', 'small'];
function byDayOf(entries, cut = null) {
  const byDay = new Map();
  const slot = day => { if (!byDay.has(day)) byDay.set(day, Object.fromEntries(CAL_KINDS.map(k => [k.key, []]))); return byDay.get(day); };
  for (const e of entries) {
    if (e.onDay) { slot(e.onDay)[e.kind].push(e); continue; }
    const recorded = cut && cut.days && JOB_KINDS.includes(e.kind);
    const d = new Date(e.from + 'T12:00:00'), end = new Date((e.to || e.from) + 'T12:00:00');
    // A runaway or reversed pair must not spin here.
    for (let i = 0; d <= end && i < 400; i++, d.setDate(d.getDate() + 1)) {
      const day = ymd(d);
      if (!e.anyDay && isWeekend(day)) continue;   // nobody is manning a weekend on this board
      if (recorded && cut.days.has(day)) continue;   // the record speaks for that day
      slot(day)[e.kind].push(e);
    }
  }
  return byDay;
}

// Twelve months from this one, as calendars.
function monthsAhead(n, from) {
  const out = [];
  const c = from ? new Date(from + 'T12:00:00') : new Date(); c.setDate(1);
  // Starting in last month (the week before this one began there) adds that month on top of the twelve.
  if (from && c.getMonth() !== new Date().getMonth()) n += 1;
  for (let i = 0; i < n; i++) { out.push(new Date(c)); c.setMonth(c.getMonth() + 1); }
  return out;
}

// The days the List and the Week/Day views cover.
function periodWindow(period, focus, today) {
  if (period === 'day') return { from: focus, to: focus };
  if (period === 'week') { const s = weekStartOf(focus); return { from: s, to: addDaysYmd(s, 6) }; }
  const m = new Date(today + 'T12:00:00'); m.setDate(1);
  const end = new Date(m); end.setMonth(end.getMonth() + 12); end.setDate(0);
  return { from: ymd(m), to: ymd(end) };
}
const periodLabel = (period, focus) => {
  const f = s => new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (period === 'day') return new Date(focus + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const s = weekStartOf(focus);
  return `${f(s)} – ${f(addDaysYmd(s, 6))}`;
};

// What opens an entry: the job's schedule, the card, the bid, the time-off page.
const entryPath = e => e.job ? `/admin/jobs/${e.job.id}/schedule`
  : e.wo ? `/admin/workflows/small_jobs?card=${e.wo.id}`
  : e.card ? `/admin/workflows/${e.card.type}?card=${e.card.id}`
  : e.bid ? `/admin/bids/${e.bid.id}` : e.off ? '/admin/employees/time-off'
  : e.shop ? `/admin/shop?tab=shop_order&order=${e.shop.id}` : null;

// The little tag beside an entry: its Small Jobs column, the drill, the bid's stage, the kind of time off.
function EntryTag({ e, boardOnly = false }) {
  const k = CAL_KIND[e.kind];
  const pill = (text, color, bg, border) => (
    <span style={{ alignSelf: 'center', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', borderRadius: 999, padding: '2px 9px', color, background: bg, border: `1px solid ${border}` }}>{text}</span>
  );
  if (e.wo) {
    // The Small Jobs board column, always — a filler mark never changes it (Ryan, Nov 1 2026); on the
    // calendar the filler word sits beside it, in the List it has its own column (boardOnly).
    const col = WO_COLUMNS.find(c => c.key === (e.wo.status || 'next_week'));
    const fl = !boardOnly && e.fillerLine ? pill(e.fillerLine === 'dirt' ? 'Dirt filler' : 'Electrical filler', (CAL_KIND[e.fillerLine] || k).ink, '#fff', (CAL_KIND[e.fillerLine] || k).color) : null;
    return <>{col ? pill(col.label, col.color, `${col.color}14`, `${col.color}55`) : null}{fl}</>;
  }
  if (e.filler) return pill('Filler', k.ink, '#fff', k.color);
  if (e.extra) return pill('Added', k.ink, '#fff', k.color);
  const tag = e.card ? (e.card.rig || null) : e.bid ? (BID_WORD[e.bid.status] || e.bid.status) : e.off ? (OFF_WORD[e.off.kind] || 'Time off')
    : e.shop ? (e.shop.assignee_name || (SHOP_ORDER_META[e.shop.type] || {}).label || null) : null;
  return tag ? pill(tag, k.ink, k.soft, k.color) : null;
}

// Dirt and Electrical only show their own side (Ryan, Oct 30 2026): operators are dirt, electricians
// electrical, laborers either way. Without a supervisor of the line's own trade on the job that day,
// the laborers there are most likely doing the other line's work — so nobody is counted, and it says so.
const LINE_TRADES = { dirt: ['Operator', 'Operator Apprentice'], elec: ['Electrician', 'Electrical Apprentice'] };
// Which kinds share people (Ryan, Oct 30 2026).
const CREW_GROUPS = { electrical: ['elec', 'loops', 'small'], dirt: ['dirt', 'vert', 'horiz'] };
// Filler work goes to the bottom of a day's list (Ryan, Oct 30 2026) — unless someone of its trade is
// on it that day (fillerSort in CalendarView, Nov 1 2026).
// Drilling, horizontal and loops crews are the people scheduled to that card — a QuickBooks Time shift
// titled with its name (Ryan, Oct 31 2026) — and they don't show on the job's Dirt / Electrical too.
// Nobody scheduled to the card = nobody on it.
const KIND_TAG = { vert: 'vertical_drilling', horiz: 'horizontal_drilling', loops: 'loops' };
const jobCrewOnly = people => people.filter(p => !p.tag);
function crewFor(c, kind) {
  if (!c || !Array.isArray(c.people)) return c;
  if (KIND_TAG[kind]) {
    const mine = c.people.filter(p => p.tag === KIND_TAG[kind]);
    return { ...c, crew: mine.length, supervisors: mine.filter(p => p.supervisor), people: mine };
  }
  if (!LINE_TRADES[kind]) return c;
  const job = jobCrewOnly(c.people);
  // No trade set counts for either side, like a laborer — better shown twice than hidden (Ryan, Nov 4 2026).
  const own = p => LINE_TRADES[kind].includes(p.trade), either = p => p.trade === 'Laborer' || !p.trade;
  const side = job.filter(p => own(p) || either(p));
  // No supervisor of the trade: the tile says so AND still lists who is on it (Ryan, Nov 4 2026: Alex and
  // Miguel called in sick on Prater, and Oscar vanished from the job). crew -1 = people on the job, none of them this side.
  if (!job.some(p => p.supervisor && own(p))) return { ...c, crew: side.length || (job.length ? -1 : 0), supervisors: [], people: side, noLineSup: true };
  return { ...c, crew: side.length, supervisors: side.filter(p => p.supervisor), people: side };
}
// The people a row counts as its own — what crewFor counts, but kept even when there's no line supervisor.
function peopleFor(c, kind) {
  if (!c || !Array.isArray(c.people)) return [];
  if (KIND_TAG[kind]) return c.people.filter(p => p.tag === KIND_TAG[kind]);
  const job = jobCrewOnly(c.people);
  if (!LINE_TRADES[kind]) return job;
  return job.filter(p => LINE_TRADES[kind].includes(p.trade) || p.trade === 'Laborer' || !p.trade);
}
// A phone-width screen (Ryan, Nov 1 2026: "clean that up for the mobile view"): the header's stacks lie
// flat in rows, the week shows one day under another, the Day rows wrap. Nothing else changes.
function useNarrow(px = 760) {
  const [narrow, setNarrow] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < px);
  React.useEffect(() => {
    const on = () => setNarrow(window.innerWidth < px);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [px]);
  return narrow;
}
const PERSON_DRAG = 'application/x-titan-person';
// Filler work is "assigned" once someone of its own trade is on the schedule for the job that day.
const assignedFor = (c, kind) => !!(c && Array.isArray(c.people) && LINE_TRADES[kind] && jobCrewOnly(c.people).some(p => LINE_TRADES[kind].includes(p.trade)));
// Filler with nobody of its trade on it that day shows light and washed-out (Ryan, Oct 30 2026).
const fillerFade = { opacity: 0.45, filter: 'saturate(0.4)' };
const noLineSupWord = kind => `no ${kind === 'dirt' ? 'dirt' : 'electrical'} supervisor`;

// One row in a day's list (the square's window and the Day view): who's scheduled from QuickBooks
// Time, and the 🗓️ Schedule button for job work.
function EntryRow({ e, day, dayCrew, onGo, onSchedule, onRemove, onMove, narrow = false }) {
  const light = e.filler && !(dayCrew && dayCrew.byJob && assignedFor(dayCrew.byJob[e.projectId], e.fillerLine || e.kind));
  const [over, setOver] = React.useState(false);
  if (onMove) return <DayRow e={e} day={day} dayCrew={dayCrew} onGo={onGo} onSchedule={onSchedule} onRemove={onRemove} onMove={onMove} light={light} over={over} setOver={setOver} narrow={narrow} />;
  return (
    <div role="button" tabIndex={0} onClick={() => onGo(e)}
      style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 4px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', ...(light ? fillerFade : null) }}>
      {e.number && <b style={{ fontSize: 13.5, minWidth: 52 }}>{e.number}</b>}
      <EntryTag e={e} />
      <span style={{ fontSize: 13, color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
      <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{shortDate(e.from)}{e.to && e.to !== e.from ? ` – ${shortDate(e.to)}` : ''}</span>
      {e.pm && <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{e.pm}</span>}
      {e.projectId && (() => {
        if (dayCrew === 'loading') return <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', minWidth: 120 }}>…</span>;
        if (!dayCrew || dayCrew === 'error') return null;
        const c = crewFor(dayCrew.byJob[e.projectId], e.kind);
        if (!c || !c.crew) return <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', minWidth: 120 }}>nobody scheduled</span>;
        if (c.noLineSup) return <span style={{ fontSize: 12, whiteSpace: 'nowrap', minWidth: 120, fontWeight: 700, color: '#b45309' }} title={(c.people || []).map(p => p.name).join(', ')}>{noLineSupWord(e.kind)}{c.crew > 0 && <span style={{ fontWeight: 500, color: '#6b7280' }}> · {c.crew}</span>}</span>;
        return (
          <span title={`${c.crew} scheduled on this job that day`} style={{ fontSize: 12, whiteSpace: 'nowrap', minWidth: 120, fontWeight: 700, color: c.supervisors.length ? '#1e3a5f' : '#b45309' }}>
            {c.supervisors.length ? `👷 ${c.supervisors.map(x => x.name + (x.draft ? ' (draft)' : '')).join(', ')}` : 'no supervisor'}
            <span style={{ fontWeight: 500, color: '#6b7280' }}> · {c.crew}</span>
          </span>
        );
      })()}
      {/* Put people on this job in QuickBooks Time from this day — same window as the
          drilling / loops / small-jobs cards; time off is never moved. */}
      {e.extra && onRemove && canAddToSchedule() && (
        <button onClick={ev => { ev.stopPropagation(); onRemove(e, day); }} title="Take this added day off the calendar (QuickBooks Time is not changed)"
          style={{ alignSelf: 'center', padding: '4px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>✕ Take off</button>
      )}
      {e.projectId && canAddToSchedule() && (
        <button onClick={ev => { ev.stopPropagation(); onSchedule(e, day); }} title="Put people on this job in QuickBooks Time"
          style={{ alignSelf: 'center', padding: '4px 10px', borderRadius: 7, border: '1.5px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>🗓️ Schedule</button>
      )}
    </div>
  );
}

// The Day view's row (Ryan, Oct 31 2026): number, name, dates and PM lined up in columns on the left;
// everyone on the job that day as a pill on the right, supervisors first. Drag a pill onto another
// job, say OK, and their QuickBooks Time shift for the day moves there.
function DayRow({ e, day, dayCrew, onGo, onSchedule, onRemove, onMove, light, over, setOver, narrow = false }) {
  const k = CAL_KIND[e.kind] || {};
  const can = canAddToSchedule();
  const dropOk = can && e.projectId && JOB_KINDS.includes(e.kind);
  const raw = dayCrew && dayCrew !== 'loading' && dayCrew !== 'error' && e.projectId ? dayCrew.byJob[e.projectId] : null;
  const c = raw ? crewFor(raw, e.kind) : null;
  const people = peopleFor(raw, e.kind).slice().sort((a, b) => (b.supervisor ? 1 : 0) - (a.supervisor ? 1 : 0));
  const col = { fontSize: 12.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 };
  return (
    <div role="button" tabIndex={0} onClick={() => onGo(e)}
      onDragOver={dropOk ? ev => { if ([...ev.dataTransfer.types].includes(PERSON_DRAG)) { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; if (!over) setOver(true); } } : undefined}
      onDragLeave={dropOk ? ev => { if (!ev.currentTarget.contains(ev.relatedTarget)) setOver(false); } : undefined}
      onDrop={dropOk ? ev => { ev.preventDefault(); setOver(false); let p = null; try { p = JSON.parse(ev.dataTransfer.getData(PERSON_DRAG)); } catch { return; } if (p) onMove(p, e, day); } : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: narrow ? 8 : 12, flexWrap: narrow ? 'wrap' : 'nowrap', padding: '8px 6px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', borderRadius: 6,
        ...(over ? { background: k.soft || '#eff6ff', outline: `2px dashed ${k.color || '#1e3a5f'}` } : null), ...(light && !over ? fillerFade : null) }}>
      <b style={{ fontSize: 13.5, width: narrow ? 'auto' : 92, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.number || ''}</b>
      <div style={{ flex: narrow ? '1 1 200px' : '0 1 330px', display: 'flex', alignItems: 'center', gap: 6, minWidth: narrow ? 0 : 140 }}>
        <EntryTag e={e} />
        <span title={e.name} style={{ fontSize: 13, color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
      </div>
      <span style={{ ...col, width: narrow ? 'auto' : 110 }}>{shortDate(e.from)}{e.to && e.to !== e.from ? ` – ${shortDate(e.to)}` : ''}</span>
      <span title={e.pm || ''} style={{ ...col, width: narrow ? 'auto' : 130 }}>{e.pm || ''}</span>
      {/* On a phone the people take a full row of their own, left to right, so they never run over the
          dates and PM beside them (Ryan, Nov 1 2026, from his phone). */}
      <div style={{ flex: narrow ? '1 1 100%' : 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: narrow ? 'flex-start' : 'flex-end', alignItems: 'center' }}>
        {e.projectId && dayCrew === 'loading' && <span style={{ fontSize: 12, color: '#9ca3af' }}>…</span>}
        {raw !== null && c && c.noLineSup && <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', whiteSpace: 'nowrap' }}>{noLineSupWord(e.kind)}</span>}
        {e.projectId && dayCrew && dayCrew !== 'loading' && dayCrew !== 'error' && !people.length && !(c && c.noLineSup) && <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>nobody scheduled</span>}
        {people.map(p => (
          <span key={`${p.tsId || p.name}|${p.tag || ''}`} draggable={can && !!p.tsId}
            onClick={ev => ev.stopPropagation()}
            onDragStart={ev => { ev.stopPropagation(); ev.dataTransfer.effectAllowed = 'move';
              ev.dataTransfer.setData(PERSON_DRAG, JSON.stringify({ tsId: p.tsId, name: p.name, fromProjectId: e.projectId, fromKind: e.kind, fromNumber: e.number || '' })); }}
            title={`${p.name}${p.trade ? ' · ' + p.trade : ''}${p.supervisor ? ' · Supervisor' : ''}${p.draft ? ' (draft)' : ''}${can ? ' — drag onto another job to move them there for the day' : ''}`}
            style={{ fontSize: 12, fontWeight: p.supervisor ? 800 : 600, padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap', cursor: can ? 'grab' : 'default',
              background: p.supervisor ? (k.color || '#1e3a5f') : (k.soft || '#f3f4f6'), color: p.supervisor ? (k.onColor || '#fff') : (k.ink || '#374151'),
              border: `1.5px solid ${k.color || '#cbd5e1'}`, opacity: p.draft ? 0.6 : 1 }}>{p.supervisor ? '👷 ' : ''}{p.name}{p.draft ? ' (draft)' : ''}</span>
        ))}
      </div>
      {e.extra && onRemove && can && (
        <button onClick={ev => { ev.stopPropagation(); onRemove(e, day); }} title="Take this added day off the calendar (QuickBooks Time is not changed)"
          style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>✕ Take off</button>
      )}
      {e.projectId && can && (
        <button onClick={ev => { ev.stopPropagation(); onSchedule(e, day); }} title="Put people on this job in QuickBooks Time"
          style={{ padding: '4px 10px', borderRadius: 7, border: '1.5px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>🗓️ Schedule</button>
      )}
    </div>
  );
}

// Who's scheduled on each job on a day in QuickBooks Time — the Field Supervisor by name (Ryan, Oct 25 2026).
function useDayCrew(day, reload = 0) {
  const [dayCrew, setDayCrew] = React.useState(null);   // { date, byJob } | 'loading' | 'error'
  React.useEffect(() => {
    if (!day) { setDayCrew(null); return; }
    setDayCrew(cur => (cur && cur !== 'loading' && cur !== 'error' && cur.date === day ? cur : 'loading'));   // a reload keeps the pills up meanwhile
    apiFetch(`/big-picture/day-crew?date=${day}`).then(setDayCrew).catch(() => setDayCrew('error'));
  }, [day, reload]);
  return dayCrew;
}
// The same, for several days at once — the week view, when one kind is showing (Ryan, Oct 30 2026).
// { [day]: { date, byJob } | 'error' }; a day not answered yet is simply missing.
function useWeekCrew(days, reload = 0) {
  const [byDay, setByDay] = React.useState({});
  const key = days.join(',') + '#' + reload;
  React.useEffect(() => {
    setByDay({});
    if (!days.length) return undefined;
    let live = true;
    for (const d of days) {
      apiFetch(`/big-picture/day-crew?date=${d}`)
        .then(r => { if (live) setByDay(m => ({ ...m, [d]: r })); })
        .catch(() => { if (live) setByDay(m => ({ ...m, [d]: 'error' })); });
    }
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return byDay;
}
// Who is on a job that day, short: the supervisors by name, then how many others.
// The one line every week tile carries (Ryan, Nov 1 2026, "for planning"): the field supervisor(s)
// of that line on the job that day, and how many more are with them. Nobody on it → nothing.
// A name on a week tile that can be dragged onto another job that day (Ryan, Nov 2 2026: "I can't drag
// people between jobs on the week view") — `onDrag(person, event)` starts it, `onDrag(null, event)` ends it.
function DragName({ p, onDrag, children, style }) {
  const can = !!(onDrag && p.tsId);
  return (
    <span draggable={can} style={{ ...style, cursor: can ? 'grab' : undefined }}
      title={can ? `${p.name} — drag onto another job to move them there for the day` : undefined}
      onDragStart={can ? ev => { ev.stopPropagation(); onDrag(p, ev); } : undefined}
      onDragEnd={can ? ev => onDrag(null, ev) : undefined}>{children}</span>
  );
}
function SupLine({ c: raw, kind, onDrag }) {
  if (raw === undefined) return <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>…</div>;
  if (!raw || raw === 'error') return null;
  const c = crewFor(raw, kind);
  if (!c.crew) return null;
  const line = { fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  if (c.noLineSup) return <div style={{ ...line, fontWeight: 700, color: '#b45309' }} title={(c.people || []).map(p => p.name).join(', ')}>{noLineSupWord(kind)}{c.crew > 0 && <span style={{ color: '#374151', fontWeight: 600 }}> · {c.crew}</span>}</div>;
  const others = c.crew - c.supervisors.length;
  return (
    <div style={line} title={c.people.map(p => p.name).join(', ')}>
      {c.supervisors.length
        ? <span style={{ fontWeight: 800, color: '#1e3a5f' }}>👷 {c.supervisors.map((x, i) => <React.Fragment key={x.tsId || x.name}>{i ? ', ' : ''}<DragName p={x} onDrag={onDrag}>{x.name}</DragName></React.Fragment>)}</span>
        : <span style={{ fontWeight: 700, color: '#b45309' }}>no supervisor</span>}
      {others > 0 && <span style={{ color: '#374151', fontWeight: 600 }}> · +{others}</span>}
    </div>
  );
}
function CrewLine({ c: raw, kind, onDrag }) {
  if (raw === undefined) return <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>…</div>;
  if (!raw || raw === 'error') return null;
  const c = crewFor(raw, kind);
  if (!c.crew) return <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>nobody scheduled</div>;
  if (c.noLineSup) return (
    <div style={{ fontSize: 12, marginTop: 5, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={(c.people || []).map(p => p.name).join(', ')}>
      <span style={{ fontWeight: 700, color: '#b45309' }}>{noLineSupWord(kind)}</span>
      {c.crew > 0 && <span style={{ color: '#374151', fontWeight: 600 }}> · {c.people.map((x, i) => <React.Fragment key={x.tsId || x.name}>{i ? ', ' : ''}<DragName p={x} onDrag={onDrag}>{x.name}</DragName></React.Fragment>)}</span>}
    </div>
  );
  const others = c.crew - c.supervisors.length;
  return (
    // At most two lines, so every week tile is the same height (Ryan, Oct 31 2026); the full crew is in the tile's window.
    <div style={{ fontSize: 12, marginTop: 5, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      {c.supervisors.length
        ? <span style={{ fontWeight: 800, color: '#1e3a5f' }}>👷 {c.supervisors.map((x, i) => <React.Fragment key={x.tsId || x.name}>{i ? ', ' : ''}<DragName p={x} onDrag={onDrag}>{x.name + (x.draft ? ' (draft)' : '')}</DragName></React.Fragment>)}</span>
        : <span style={{ fontWeight: 700, color: '#b45309' }}>no supervisor</span>}
      {others > 0 && <span style={{ color: '#374151', fontWeight: 600 }}> · +{others}</span>}
    </div>
  );
}
// Everyone on a job that day, each name coloured by the kind of work (Ryan, Oct 31 2026: the Field tab).
// Supervisors first, with the hard hat.
function CrewChips({ c: raw, kind, hidden = () => false, onDrag }) {
  if (raw === undefined) return <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>…</div>;
  if (!raw || raw === 'error') return null;
  const c = crewFor(raw, kind);
  if (!c.crew) return <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>nobody scheduled</div>;
  const k = CAL_KIND[kind];
  const list = (Array.isArray(c.people) ? c.people : []).filter(p => !hidden(p)).sort((a, b) => (b.supervisor ? 1 : 0) - (a.supervisor ? 1 : 0));
  const noSup = c.noLineSup ? <div style={{ fontSize: 12, marginTop: 5, fontWeight: 700, color: '#b45309' }}>{noLineSupWord(kind)}</div> : null;
  if (!list.length) return <>{noSup}{c.crew > 0 && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>{c.crew} hidden</div>}</>;
  return (<>
    {noSup}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 5 }}>
      {list.map(p => (
        <span key={p.tsId || p.name} title={`${p.name}${p.trade ? ' · ' + p.trade : ''}${p.supervisor ? ' · Supervisor' : ''}${p.draft ? ' (draft)' : ''}${onDrag && p.tsId ? ' — drag onto another job to move them there for the day' : ''}`}
          draggable={!!(onDrag && p.tsId)}
          onDragStart={onDrag && p.tsId ? ev => { ev.stopPropagation(); onDrag(p, ev); } : undefined}
          onDragEnd={onDrag && p.tsId ? ev => onDrag(null, ev) : undefined}
          style={{ fontSize: 11, fontWeight: p.supervisor ? 800 : 600, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap', cursor: onDrag && p.tsId ? 'grab' : undefined,
            background: k.color, color: k.onColor, opacity: p.draft ? 0.6 : 1 }}>{p.supervisor ? '👷 ' : ''}{p.name}</span>
      ))}
    </div>
  </>);
}
// Weekdays from a day through the entry's last day (20 at most — the Schedule window's limit).
const workdaysTo = (from, to) => { let n = 0; for (const d = new Date(from + 'T12:00:00'); ymd(d) <= (to || from) && n < 20; d.setDate(d.getDate() + 1)) if (d.getDay() % 6) n++; return Math.max(1, n); };

// Month = the count squares; Week = names in seven day columns; Day = the day's full list by kind.
function CalendarView({ crewRefresh = 0, narrow = false, entries, cut, kinds, today, period, focus, onFocus, onPeriod, onGo, allEntries = [], fieldPeople = [], fieldMode = false, personHidden = () => false, timeOff = [], jobs: allJobs = [], onAddDays, onRemoveDay, quickMove = false }) {
  const byDay = React.useMemo(() => byDayOf(entries, cut), [entries, cut]);
  const byDayAll = React.useMemo(() => byDayOf(allEntries, cut), [allEntries, cut]);
  const [reload, setReload] = React.useState(0);       // read the crews again after scheduling someone
  const [assign, setAssign] = React.useState(null);    // { person, day } — a name clicked in the Not-scheduled tile
  const [jobQ, setJobQ] = React.useState('');          // the job search in that window
  const dragPerson = React.useRef(null);               // { tsId, name, day } while a name is being dragged
  const canSched = canAddToSchedule();
  // Days gone by read as the record, not the plan: a little faded, still clickable.
  const pastFade = d => (d < today ? { opacity: 0.6 } : null);
  const [pick, setPick] = React.useState(null);   // { day, line } — the square's list (month view)
  const [chip, setChip] = React.useState(null);   // { e, day } — one job clicked on the week view (Ryan, Oct 29 2026)
  const [sched, setSched] = React.useState(null); // { projectId, workflow, day, days } — the Schedule window
  const [crewReload, setCrewReload] = React.useState(0);
  // The parent published a week's time off: read every day's crews again.
  React.useEffect(() => { if (crewRefresh) { setCrewReload(n => n + 1); setReload(n => n + 1); } }, [crewRefresh]);
  const dayCrew = useDayCrew(pick ? pick.day : chip ? chip.day : period === 'day' ? focus : null, crewReload);
  // A pill dropped on another job on the Day view (Ryan, Oct 31 2026): one OK, then their shift moves.
  // In Quick Move Mode (Nov 2 2026) there is no OK box — the one shift moves and a small note says so.
  const [movedNote, setMovedNote] = React.useState('');
  React.useEffect(() => { if (!movedNote) return undefined; const t = setTimeout(() => setMovedNote(''), 4000); return () => clearTimeout(t); }, [movedNote]);
  const movePerson = (p, to, day) => {
    if (!p || !p.tsId) return;
    if (p.free) { placePerson(to, day, p); return; }   // a name from the Not-scheduled list (Nov 1 2026): the Schedule window, them picked
    if (String(p.fromProjectId) === String(to.projectId) && p.fromKind === to.kind) return;
    const lbl = kind => (CAL_KIND[kind] ? CAL_KIND[kind].label : '');
    if (!quickMove && !window.confirm(`Move ${p.name} from ${p.fromNumber || 'that job'} (${lbl(p.fromKind)}) to ${to.number || 'this job'} (${lbl(to.kind)}) on ${shortDate(day)}?\n\nTheir QuickBooks Time shift moves — same hours.`)) return;
    apiFetch('/big-picture/move-person', { method: 'POST', body: JSON.stringify({ date: day, personId: p.tsId, personName: p.name,
      fromProjectId: p.fromProjectId, fromKind: p.fromKind, toProjectId: to.projectId, toKind: to.kind }) })
      .then(() => { setCrewReload(n => n + 1); setReload(n => n + 1); if (quickMove) setMovedNote(`✓ ${p.name} moved to ${to.number || 'this job'} · ${shortDate(day)}`); })
      .catch(err => { window.alert(err?.error || `${p.name} did not move.`); setCrewReload(n => n + 1); });
  };
  const movedToast = movedNote ? (
    <div style={{ position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 5000, background: '#166534', color: '#fff', fontWeight: 800, fontSize: 14, padding: '10px 18px', borderRadius: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>{movedNote}</div>
  ) : null;
  const schedule = (e, day) => { setPick(null); setChip(null); setSched({ projectId: e.projectId, workflow: e.workflow, day, days: workdaysTo(day, e.to) }); };
  // A supervisor from the Not-scheduled tile onto a job — dragged or picked (Ryan, Oct 31 2026): the
  // Schedule window opens on that job, that day, with them already picked; Review → Confirm as always.
  // In Quick Move Mode (Nov 2 2026) there is no window: they go on that job for that one day, 7:00–3:30,
  // the same write the window's Confirm does. On time off that day, they are left alone and the note says so.
  const placePerson = (e, day, person) => {
    setAssign(null);
    if (!quickMove) { setSched({ projectId: e.projectId, workflow: e.workflow, day, days: 1, people: [{ id: person.tsId, name: person.name }] }); return; }
    apiFetch('/schedule/card-crew', { method: 'POST', body: JSON.stringify({ projectId: e.projectId, workflow: e.workflow, dates: [day], personIds: [String(person.tsId)], start: '07:00', end: '15:30', confirm: true }) })
      .then(r => {
        const t = (r && r.totals) || {};
        if (t.failed) window.alert(`QuickBooks Time did not take ${person.name}: ${((r.failed || [])[0] || {}).error || 'try again'}.`);
        else setMovedNote(t.off ? `🌴 ${person.name} is on time off ${shortDate(day)} — left as is` : t.already ? `${person.name} was already on ${e.number || 'this job'} ${shortDate(day)}` : `✓ ${person.name} put on ${e.number || 'this job'} · ${shortDate(day)} · 7:00–3:30`);
        setCrewReload(n => n + 1); setReload(n => n + 1);
      })
      .catch(err => { window.alert(err?.error || `${person.name} did not go on the schedule.`); setCrewReload(n => n + 1); });
  };
  // A job from the search that isn't on the calendar that day (Ryan, Oct 31 2026): once the people are
  // confirmed into QuickBooks Time, the job goes on the calendar on those days, on the line picked.
  const placeOnJob = (j, line, day, person) => { setAssign(null); setSched({ projectId: j.id, workflow: 'job', day, days: 1, people: [{ id: person.tsId, name: person.name }], extra: { projectId: j.id, line } }); };
  const removeDay = (e, day) => {
    if (!onRemoveDay || !window.confirm(`Take ${e.number || 'this job'} off the calendar on ${shortDate(day)}?\n\nWho's scheduled in QuickBooks Time stays as it is.`)) return;
    setChip(null); setPick(null); onRemoveDay(e, day);
  };
  // Who a tile counts as on it: nobody when it says "no … supervisor".
  const tilePeople = c => (c && Array.isArray(c.people) ? c.people : []);
  // Filler work with someone of its own trade on it that day (an operator for dirt, an electrician for
  // electrical) is work for the day (Ryan, Nov 1 2026): it sorts in with the ordinary work and shows in
  // full colour, keeping its Filler tag. Filler nobody of the trade is on stays light, at the bottom.
  const isFillerNow = (e, crewDay) => !!e.filler && !(crewDay && crewDay !== 'loading' && crewDay !== 'error' && crewDay.byJob && assignedFor(crewDay.byJob[e.projectId], e.fillerLine || e.kind));
  const fillerSort = (list, crewDay) => list.filter(e => !isFillerNow(e, crewDay)).concat(list.filter(e => isFillerNow(e, crewDay)));
  // The Field tab (Ryan, Oct 31 2026): a job whose people that day are all in unticked trades drops off.
  const fieldHidden = (e, crewDay) => {
    if (!fieldMode || !e.projectId || !JOB_KINDS.includes(e.kind) || !crewDay || crewDay === 'error' || crewDay === 'loading' || !crewDay.byJob) return false;
    const ppl = tilePeople(crewFor(crewDay.byJob[e.projectId], e.kind));
    return ppl.length > 0 && ppl.every(personHidden);
  };
  // Field people on no tile that day — not off, not in an unticked trade (Ryan, Oct 31 2026: "not on a Big
  // Picture tile"). The Office tab lists the supervisors; the Field tab everyone in the field.
  const notScheduled = (d, crewDay) => {
    if (!crewDay || crewDay === 'error' || !crewDay.byJob) return null;
    const onTile = new Set();
    const l = byDayAll.get(d);
    for (const k of JOB_KINDS) for (const e of (l && l[k]) || []) {
      if (!e.projectId) continue;
      for (const x of tilePeople(crewFor(crewDay.byJob[e.projectId], k))) { if (x.userId) onTile.add(x.userId); if (x.tsId) onTile.add('ts:' + x.tsId); }
    }
    const placed = x => onTile.has(x.userId) || (x.tsId && onTile.has('ts:' + x.tsId));
    // A shift that day on anything at all — a job with no tile today, a card's shift on a day the card
    // isn't showing, someone with no trade set (Ryan, Nov 3 2026: Manuel moved to Talus, still "unscheduled").
    // They are working, so they are not free; they are named under the box with where they are.
    const busy = crewDay.busy || {};
    const working = x => !!(x.tsId && busy[x.tsId]);
    const offTs = new Set(crewDay.offIds || []);
    const offApp = new Set(timeOff.filter(t => t.status === 'approved' && t.start_date <= d && (t.end_date || t.start_date) >= d).map(t => t.user_id));
    const shown = fieldPeople.filter(x => !personHidden(x));
    const isOff = x => (x.tsId && offTs.has(x.tsId)) || offApp.has(x.userId);
    return {
      free: shown.filter(x => !placed(x) && !working(x) && !isOff(x)),
      off: shown.filter(x => !placed(x) && isOff(x)),
      elsewhere: shown.filter(x => !placed(x) && !isOff(x) && working(x)).map(x => ({ ...x, jobs: busy[x.tsId].jobs || [] })),
    };
  };
  const go = e => { setPick(null); setChip(null); onGo(e); };
  // One kind of job work showing on the week: bigger tiles with who's on each job each day.
  // Bigger tiles with who's on each job when every job switch that's on belongs to ONE crew group
  // (Ryan, Oct 30 2026): the electrical crews — Electrical, Loops, Small Jobs — or the dirt crews —
  // Dirt, Vertical and Horizontal Drilling. Bids and Vacations don't count either way.
  const jobKinds = kinds.filter(k => !['bids', 'off', 'shop'].includes(k.key)).map(k => k.key);
  const single = period === 'week' && jobKinds.length > 0
    && Object.values(CREW_GROUPS).some(g => jobKinds.every(k => g.includes(k)));
  const crewDays = React.useMemo(() => {
    if (period !== 'week') return [];
    const s = weekStartOf(focus);
    // Every weekday, and a weekend day with work on it: each tile names its field supervisor now
    // (Ryan, Nov 1 2026, "for planning"), the Not-scheduled tile needs every weekday ahead, and
    // filler shades by who is on it.
    return [0, 1, 2, 3, 4, 5, 6].map(i => addDaysYmd(s, i)).filter(d => !isWeekend(d) || !!byDay.get(d));
  }, [single, period, focus, byDay, kinds, fieldPeople.length, today, fieldMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const weekCrew = useWeekCrew(crewDays, reload);

  const cell = { background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '5px 6px', minHeight: 46 };
  const off = { ...cell, background: '#fafafa', border: '1px solid transparent' };
  const pill = k => ({ display: 'inline-block', fontSize: 12, fontWeight: 700, borderRadius: 5, padding: '1px 7px', marginRight: 3, marginBottom: 2,
    cursor: 'pointer', border: `1.5px solid ${k.color}`, background: k.soft, color: k.ink });
  const window_ = <>{sched && <AddToScheduleWindow key={`${sched.projectId}|${sched.day}|${(sched.people || []).map(x => x.id).join(',')}`} projectId={sched.projectId} workflow={sched.workflow} startDate={sched.day} days={sched.days} people={sched.people} onClose={() => { setSched(null); setReload(n => n + 1); }}
    onConfirmed={sched.extra && onAddDays ? dates => onAddDays(sched.extra.projectId, sched.extra.line, dates) : undefined} />}</>;

  // The Not-scheduled tile: field people on no job that day, each a name to drag onto a job or click
  // to pick one. On the week view in every day's column; on the Day view (Ryan, Nov 1 2026) one wide
  // box above the filler work, so people can be moved onto the jobs from there too.
  const nsTileFor = (d, ns, big) => ns && (
    <div key="__ns" style={{ border: '1.5px dashed #94a3b8', borderRadius: big ? 10 : single ? 8 : 6, padding: big ? '10px 14px' : single ? '7px 8px' : '4px 6px', marginBottom: big ? 12 : single ? 6 : 4, background: '#fff' }}>
      <div style={{ fontSize: big ? 14 : 11.5, fontWeight: 800, color: '#334155', marginBottom: big ? 8 : 4 }}>
        👷 Not scheduled{ns !== 'loading' && ns.free ? ` · ${ns.free.length}` : ''}
        {big && canSched && ns !== 'loading' && ns.free && ns.free.length > 0 && <span style={{ fontWeight: 600, color: '#9ca3af', fontSize: 12.5 }}> — drag a name onto a job, or click one to pick a job</span>}
      </div>
      {ns === 'loading' ? <div style={{ fontSize: big ? 13 : 11.5, color: '#9ca3af' }}>…</div>
        : !ns.free ? <div style={{ fontSize: big ? 13 : 11.5, color: '#9ca3af' }}>QuickBooks Time didn't answer</div>
        : !ns.free.length ? <div style={{ fontSize: big ? 13 : 11.5, color: '#15803d', fontWeight: 700 }}>everyone's on a job</div>
        : <div style={{ display: 'flex', flexWrap: 'wrap', gap: big ? 6 : 4 }}>
            {ns.free.map(x => {
              const can = canSched && !!x.tsId;
              return (
                <span key={x.userId} draggable={can}
                  onDragStart={ev => { dragPerson.current = { ...x, day: d }; ev.dataTransfer.effectAllowed = 'copyMove';
                    try { ev.dataTransfer.setData(PERSON_DRAG, JSON.stringify({ tsId: x.tsId, name: x.name, free: true })); ev.dataTransfer.setData('text/plain', x.name); } catch {} }}
                  onDragEnd={() => { dragPerson.current = null; }}
                  onClick={() => { if (!canSched) return; if (!x.tsId) { window.alert(`${x.name} isn't linked to QuickBooks Time yet.`); return; } setJobQ(''); setAssign({ person: x, day: d }); }}
                  title={can ? `${x.name}${x.trade ? ' · ' + x.trade : ''} — drag onto a job, or click to pick one` : x.tsId ? x.name : 'Not linked to QuickBooks Time'}
                  style={{ fontSize: big ? 12.5 : 11.5, fontWeight: 700, padding: big ? '3px 11px' : '2px 8px', borderRadius: 999, background: '#e2e8f0', color: '#1e293b', cursor: can ? 'grab' : 'default', whiteSpace: 'nowrap', userSelect: 'none' }}>
                  {x.name}
                </span>
              );
            })}
          </div>}
      {ns !== 'loading' && ns.off && ns.off.length > 0 && <div style={{ fontSize: big ? 12 : 10.5, color: '#64748b', marginTop: big ? 8 : 4 }}>🌴 off: {ns.off.map(x => x.name).join(', ')}</div>}
      {ns !== 'loading' && ns.elsewhere && ns.elsewhere.length > 0 && (
        <div style={{ fontSize: big ? 12 : 10.5, color: '#64748b', marginTop: big ? 6 : 3 }} title="Scheduled in QuickBooks Time on something that has no tile here today — a job not on the board, or a card's shift on a day the card isn't showing">
          📍 on a job, no tile here: {ns.elsewhere.map(x => big ? `${x.name} (${x.jobs.map(j => (String(j).match(/^\S+/) || [j])[0]).join(', ')})` : x.name.replace(/^(\S+)\s+(\S).*$/, '$1 $2.')).join(' · ')}
        </div>
      )}
    </div>
  );
  // "Put … on…" — the job list for a name clicked in the Not-scheduled tile (week and Day views).
  const assignWindow = assign && (() => {
    const l = byDay.get(assign.day);
    const jobs = fillerSort(kinds.filter(k => JOB_KINDS.includes(k.key)).flatMap(k => ((l && l[k.key]) || []).filter(e => e.projectId)), period === 'day' ? dayCrew : weekCrew[assign.day]);
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 6200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setAssign(null)}>
        <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: 'min(560px, 96vw)', maxHeight: '80vh', overflowY: 'auto', borderTop: '5px solid #1e3a5f' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <b style={{ fontSize: 15 }}>Put {assign.person.name} on…</b>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{new Date(assign.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
            <button onClick={() => setAssign(null)} style={{ marginLeft: 'auto', border: 'none', background: '#f3f4f6', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Close</button>
          </div>
          {/* Search every job (Ryan, Oct 31 2026): one not on the calendar that day goes on it for the days confirmed. */}
          <input value={jobQ} onChange={ev => setJobQ(ev.target.value)} placeholder="Search jobs — number or name…" autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 14, fontFamily: 'inherit' }} />
          {jobQ.trim() && (() => {
            const s = jobQ.trim().toLowerCase();
            const found = allJobs.filter(j => `${j.project_number} ${j.name}`.toLowerCase().includes(s)).slice(0, 12);
            const trade = String(assign.person.trade || '');
            const guess = /operator/i.test(trade) ? 'dirt' : /electric/i.test(trade) ? 'elec' : null;
            return (
              <div style={{ marginBottom: 12 }}>
                {!found.length && <div style={{ fontSize: 13, color: '#9ca3af', padding: '4px 2px' }}>No job matches.</div>}
                {found.map(j => (
                  <div key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', marginBottom: 6, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>
                    <b style={{ fontSize: 13 }}>{j.project_number}</b>
                    <span style={{ fontSize: 13, color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name}</span>
                    {['dirt', 'elec'].map(line => {
                      const k = CAL_KIND[line];
                      return (
                        <button key={line} type="button" onClick={() => placeOnJob(j, line, assign.day, assign.person)}
                          title={`Put ${assign.person.name} on ${j.project_number} — ${k.label}, ${shortDate(assign.day)}`}
                          style={{ padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
                            border: `1.5px solid ${k.color}`, background: guess === line ? k.color : k.soft, color: guess === line ? k.onColor : k.ink }}>{k.label}</button>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })()}
          {!jobQ.trim() && !jobs.length && <div style={{ fontSize: 13, color: '#9ca3af' }}>No jobs showing that day — search for one above.</div>}
          {!jobQ.trim() && jobs.map(e => {
            const k = CAL_KIND[e.kind];
            return (
              <button key={e.key} type="button" onClick={() => placePerson(e, assign.day, assign.person)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 10px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                  border: '1px solid #e5e7eb', borderLeft: `5px solid ${k.color}`, background: k.soft, ...(e.filler ? { opacity: 0.75 } : null) }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: k.ink, minWidth: 110 }}>{k.label}{e.filler ? ' · Filler' : ''}</span>
                <b style={{ fontSize: 13 }}>{e.number}</b>
                <span style={{ fontSize: 13, color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  })();

  if (period === 'day') {
    const l = byDay.get(focus);
    const rows = k => ((l && l[k.key]) || []).filter(e => !fieldHidden(e, dayCrew));
    // Filler work in its own cards under the Not-scheduled box (Ryan, Nov 1 2026), like the week view's
    // order — unless someone of its trade is on it that day: then it sits with the day's ordinary work.
    const groups = kinds.map(k => ({ k, list: rows(k).filter(e => !isFillerNow(e, dayCrew)) })).filter(g => g.list.length);
    const fillers = kinds.map(k => ({ k, list: rows(k).filter(e => isFillerNow(e, dayCrew)) })).filter(g => g.list.length);
    const ns = !isWeekend(focus) && focus >= today && fieldPeople.length ? (!dayCrew || dayCrew === 'loading' ? 'loading' : notScheduled(focus, dayCrew)) : null;
    const card = ({ k, list }, filler) => (
      <div key={`${k.key}${filler ? '|filler' : ''}`} style={{ background: '#fff', border: '1px solid #eee', borderLeft: `5px solid ${k.color}`, borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: k.ink, marginBottom: 2 }}>{k.label}{filler ? ' · Filler work' : ''} <span style={{ fontWeight: 600, color: '#9ca3af' }}>· {list.length}</span></div>
        {list.map(e => <EntryRow key={e.key} e={e} day={focus} dayCrew={dayCrew} onGo={go} onSchedule={schedule} onRemove={removeDay} onMove={movePerson} narrow={narrow} />)}
      </div>
    );
    return (
      <div>
        {movedToast}
        {focus < today && <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 10 }}>This day has gone by — what's shown is the record of what was scheduled on it.</div>}
        {!groups.length && !fillers.length && <div style={{ fontSize: 13.5, color: '#9ca3af', padding: '18px 4px' }}>Nothing scheduled on this day.</div>}
        {groups.map(g => card(g, false))}
        {nsTileFor(focus, ns, true)}
        {fillers.map(g => card(g, true))}
        {assignWindow}
        {window_}
      </div>
    );
  }

  if (period === 'week') {
    const start = weekStartOf(focus);
    // On a phone the days stack one under another, and an empty weekend day is left out.
    const days = [0, 1, 2, 3, 4, 5, 6].map(i => addDaysYmd(start, i)).filter(d => !narrow || !isWeekend(d) || !!byDay.get(d));
    return (
      <div>
        {movedToast}
        {/* Seven equal columns that shrink to the window (Ryan, Nov 1 2026: "all the same width, no scrolling
            side to side") — minmax(0, 1fr) so a long one-line name can't push a column wider than its share. */}
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'repeat(7, minmax(0, 1fr))', gap: 6, alignItems: 'start' }}>
          {days.map(d => {
            const l = byDay.get(d), wk = isWeekend(d), isToday = d === today;
            const all = kinds.flatMap(k => ((l && l[k.key]) || []).map(e => ({ e, k }))).filter(x => !fieldHidden(x.e, weekCrew[d]));
            const list = all.filter(x => !isFillerNow(x.e, weekCrew[d])).concat(all.filter(x => isFillerNow(x.e, weekCrew[d])));   // filler nobody's on at the bottom
            const firstFiller = list.findIndex(x => isFillerNow(x.e, weekCrew[d]));
            const ns = !wk && d >= today && fieldPeople.length ? (weekCrew[d] === undefined ? 'loading' : notScheduled(d, weekCrew[d])) : null;
            const nsTile = nsTileFor(d, ns, false);
            // A name from the Not-scheduled box (free) or from another job's tile that same day; never its own tile.
            const dropOk = e => { const x = dragPerson.current; return !!(x && x.day === d && e.projectId && JOB_KINDS.includes(e.kind) && !(x.fromProjectId && String(x.fromProjectId) === String(e.projectId) && x.fromKind === e.kind)); };
            // Dragging a name off this tile (Ryan, Nov 2 2026): remember who and where from; the drop moves their shift.
            const dragFrom = e => canSched ? (p, ev) => {
              if (!p) { dragPerson.current = null; return; }
              dragPerson.current = { tsId: p.tsId, name: p.name, day: d, fromProjectId: e.projectId, fromKind: e.kind, fromNumber: e.number || '' };
              ev.dataTransfer.effectAllowed = 'move';
              try { ev.dataTransfer.setData(PERSON_DRAG, JSON.stringify(dragPerson.current)); ev.dataTransfer.setData('text/plain', p.name); } catch { /* fine */ }
            } : null;
            return (
              <div key={d} style={{ ...(wk ? off : cell), minWidth: 0, minHeight: narrow ? 0 : 260, ...(isToday ? { border: '2px solid #111' } : null), ...pastFade(d) }}>
                <button onClick={() => { onFocus(d); onPeriod('day'); }} title="Open this day"
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0 0 6px', cursor: 'pointer' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}</span>{' '}
                  <span style={{ fontSize: 15, fontWeight: 800, color: isToday ? '#111' : '#374151' }}>{new Date(d + 'T12:00:00').getDate()}</span>
                  {list.length > 0 && <span style={{ fontSize: 11, color: '#9ca3af' }}> · {list.length}</span>}
                </button>
                {list.flatMap(({ e, k }, i) => [i === firstFiller ? nsTile : null, (
                  <div key={e.key} role="button" tabIndex={0} onClick={() => setChip({ e, day: d })}
                    onDragOver={ev => { if (dropOk(e)) { ev.preventDefault(); ev.currentTarget.style.outline = '3px solid #1e3a5f'; } }}
                    onDragLeave={ev => { ev.currentTarget.style.outline = ''; }}
                    onDrop={ev => { ev.currentTarget.style.outline = ''; if (!dropOk(e)) return; ev.preventDefault(); const x = dragPerson.current; dragPerson.current = null; if (x.fromProjectId) movePerson(x, e, d); else placePerson(e, d, x); }}
                    title={`${k.label}${e.filler ? ' · Filler work' : ''} — ${e.number ? e.number + ' ' : ''}${e.name}${e.pm ? ' · ' + e.pm : ''}`}
                    style={{ fontSize: single ? 13 : 11.5, lineHeight: 1.3, padding: single ? '8px 9px' : '4px 6px', marginBottom: single ? 6 : 4, borderRadius: single ? 8 : 6, cursor: 'pointer',
                      ...(fieldMode ? { minHeight: single ? 70 : 56 } : { height: single ? 108 : 74 }), boxSizing: 'border-box',   // every tile the same height (Ryan, Oct 31 2026), with room for the supervisor line (Nov 1 2026); the Field tab grows to fit everyone
                      background: k.soft, borderLeft: `${single ? 5 : 4}px solid ${k.color}`, color: '#1f2937', overflow: 'hidden',
                      ...(e.filler && !(weekCrew[d] && weekCrew[d] !== 'error' && assignedFor(weekCrew[d].byJob[e.projectId], e.fillerLine || e.kind)) ? fillerFade : null) }}>
                    {e.filler && <span style={{ float: 'right', fontSize: 10, fontWeight: 800, color: k.ink, border: `1px solid ${k.color}`, borderRadius: 999, padding: '0 6px', background: '#fff' }}>Filler</span>}
                    {e.number && <b style={{ color: k.ink }}>{e.number} </b>}
                    <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{e.name}</span>
                    {fieldMode && e.projectId && JOB_KINDS.includes(e.kind)
                      ? <CrewChips kind={e.kind} hidden={personHidden} onDrag={dragFrom(e)} c={weekCrew[d] === undefined ? undefined : weekCrew[d] === 'error' ? 'error' : (weekCrew[d].byJob[e.projectId] || { crew: 0, supervisors: [], people: [] })} />
                      : single && e.projectId ? <CrewLine kind={e.kind} onDrag={JOB_KINDS.includes(e.kind) ? dragFrom(e) : null} c={weekCrew[d] === undefined ? undefined : weekCrew[d] === 'error' ? 'error' : (weekCrew[d].byJob[e.projectId] || { crew: 0, supervisors: [], people: [] })} />
                      : e.projectId && JOB_KINDS.includes(e.kind) && <SupLine kind={e.kind} onDrag={dragFrom(e)} c={weekCrew[d] === undefined ? undefined : weekCrew[d] === 'error' ? 'error' : (weekCrew[d].byJob[e.projectId] || { crew: 0, supervisors: [], people: [] })} />}
                  </div>
                )])}
                {firstFiller < 0 && nsTile}
              </div>
            );
          })}
        </div>
        {assignWindow}
        {chip && (() => {
          const k = CAL_KIND[chip.e.kind];
          return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 6200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => setChip(null)}>
              <div onClick={ev => ev.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, padding: 18, width: 'min(860px, 96vw)', maxHeight: '80vh', overflowY: 'auto', borderTop: `5px solid ${k.color}` }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                  <b style={{ fontSize: 15 }}>{new Date(chip.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</b>
                  <span style={{ fontSize: 13, color: k.ink, fontWeight: 700 }}>{k.label}</span>
                  <button onClick={() => setChip(null)} style={{ marginLeft: 'auto', border: 'none', background: '#f3f4f6', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Close</button>
                </div>
                <EntryRow e={chip.e} day={chip.day} dayCrew={dayCrew} onGo={go} onSchedule={schedule} onRemove={removeDay} />
              </div>
            </div>
          );
        })()}
        {window_}
      </div>
    );
  }

  // The month view starts the week before this one (Ryan, Oct 31 2026) — not the 1st, so there's
  // no month of gone-by days on top — and runs twelve months on.
  const calStart = addDaysYmd(weekStartOf(today), -7);
  const months = monthsAhead(12, calStart);
  const pickKind = pick && CAL_KIND[pick.line];
  const detail = pick && fillerSort(byDay.get(pick.day)?.[pick.line] || [], dayCrew);
  return (
    <div>
      {months.map(m => {
        const y = m.getFullYear(), mo = m.getMonth();
        const first = new Date(y, mo, 1), last = new Date(y, mo + 1, 0);
        // Sunday-first, the way a wall calendar reads.
        const cutDay = ymd(first) < calStart ? Number(calStart.slice(8, 10)) : 1;   // calStart is a Sunday
        const lead = cutDay > 1 ? 0 : first.getDay();
        const cells = [];
        for (let i = 0; i < lead; i++) cells.push(null);
        for (let d = cutDay; d <= last.getDate(); d++) cells.push(new Date(y, mo, d));
        // The busiest day is ALL the boxes on it added up, and the average is over the working days
        // shown (Ryan, Oct 31 2026).
        let peak = 0, sum = 0, workdays = 0;
        for (const c of cells) {
          if (!c) continue;
          const l = byDay.get(ymd(c));
          const tot = l ? kinds.reduce((n, k) => n + l[k.key].length, 0) : 0;
          peak = Math.max(peak, tot);
          if (c.getDay() % 6) { sum += tot; workdays++; }
        }
        const avg = workdays ? sum / workdays : 0;
        return (
          <div key={`${y}-${mo}`} style={{ marginBottom: 18, overflowX: narrow ? 'auto' : 'visible' }}>   {/* a phone scrolls the month sideways; the squares keep their size */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{m.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              {peak > 0
                ? <span style={{ fontSize: 12, color: '#6b7280' }}>busiest day: <b style={{ color: '#374151' }}>{peak}</b> · average a day: <b style={{ color: '#374151' }}>{avg.toFixed(1)}</b></span>
                : <span style={{ fontSize: 12, color: '#9ca3af' }}>nothing scheduled</span>}
            </div>
            {peak > 0 && (<>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4, minWidth: narrow ? 620 : undefined }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, minWidth: narrow ? 620 : undefined }}>
                {cells.map((c, i) => {
                  if (!c) return <div key={i} />;
                  const key = ymd(c), l = byDay.get(key), wknd = c.getDay() === 0 || c.getDay() === 6;
                  const isToday = key === today;
                  return (
                    <div key={i} style={{ ...(wknd ? off : cell), ...(isToday ? { border: '2px solid #111', padding: '4px 5px' } : null), ...pastFade(key) }}>
                      <button onClick={() => { onFocus(key); onPeriod('day'); }} title="Open this day"
                        style={{ display: 'block', background: 'none', border: 'none', padding: 0, marginBottom: 4, cursor: 'pointer',
                          fontSize: 11, color: isToday ? '#111' : '#9ca3af', fontWeight: isToday ? 800 : 400 }}>{c.getDate()}</button>
                      {l && kinds.map(k => l[k.key].length > 0 && (
                        <span key={k.key} title={`${l[k.key].length} ${k.label.toLowerCase()}`} onClick={() => setPick({ day: key, line: k.key })}
                          style={pill(k)}>{l[k.key].length}</span>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>)}
          </div>
        );
      })}

      {pick && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 6200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setPick(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 18, width: 'min(860px, 96vw)', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
              <b style={{ fontSize: 15 }}>{new Date(pick.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</b>
              <span style={{ fontSize: 13, color: pickKind.ink, fontWeight: 700 }}>{detail.length} · {pickKind.label}</span>
              <button onClick={() => setPick(null)} style={{ marginLeft: 'auto', border: 'none', background: '#f3f4f6', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
            {detail.map(e => <EntryRow key={e.key} e={e} day={pick.day} dayCrew={dayCrew} onGo={go} onSchedule={schedule} onRemove={removeDay} />)}
          </div>
        </div>
      )}
      {window_}
    </div>
  );
}

// The List: everything the calendar shows, one row per piece of work, for the Month (the year
// ahead), the Week or the Day picked — sortable by any heading.
function EverythingList({ entries, today, onGo, rigsByType = {}, canEdit = false, onSave }) {
  const [sort, setSort] = React.useState({ key: 'from', dir: 1 });
  const onSort = key => setSort(s => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  const kindRank = Object.fromEntries(CAL_KINDS.map((k, i) => [k.key, String(i)]));
  const rank = e => sort.key === 'kind' ? kindRank[e.kind] : sort.key === 'job' ? String(e.number || '') : sort.key === 'pm' ? (e.pm || '~').toLowerCase()
    : sort.key === 'name' ? String(e.name || '').toLowerCase() : sort.key === 'to' ? e.to : e.from;
  const rows = [...entries].sort((a, b) => { const x = rank(a), y = rank(b); if (x === y) return a.from < b.from ? -1 : a.from > b.from ? 1 : 0; return (x < y ? -1 : 1) * sort.dir; });
  const td = { padding: '8px 6px', borderBottom: '1px solid #f1f3f5', fontSize: 13, verticalAlign: 'middle' };
  if (!rows.length) return <div style={{ fontSize: 13.5, color: '#9ca3af', padding: '18px 4px' }}>Nothing scheduled here.</div>;
  return (
    <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '10px 8px' }}>
      <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <SortHead label="Kind" sortKey="kind" sort={sort} onSort={onSort} width={150} />
            <SortHead label="Job" sortKey="job" sort={sort} onSort={onSort} width={80} />
            <SortHead label="Name" sortKey="name" sort={sort} onSort={onSort} />
            <th style={{ width: 150 }} />
            <SortHead label="Start" sortKey="from" sort={sort} onSort={onSort} width={canEdit ? 140 : 80} />
            {canEdit && <th style={{ width: 170, textAlign: 'left', fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', padding: '6px' }} title="Filler work on the Big Picture — the Small Jobs board column is not changed by it">Filler</th>}
            <SortHead label="Finish" sortKey="to" sort={sort} onSort={onSort} width={canEdit ? 140 : 80} />
            <SortHead label="PM" sortKey="pm" sort={sort} onSort={onSort} width={130} />
          </tr>
        </thead>
        <tbody>
          {rows.map(e => {
            const k = CAL_KIND[e.kind];
            const running = e.from <= today && today <= e.to;
            const rigs = canEdit && e.card && rigsByType[e.card.type];   // drilling cards only
            const clash = rigs && (e.card.clashes || []).length > 0;
            const editEnd = canEdit && (e.card || e.wo) && !!e.from;
            const editStart = canEdit && !!(e.card || e.wo);
            return (
              <tr key={e.key} onClick={() => onGo(e)} style={{ cursor: 'pointer' }}>
                <td style={td}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, color: k.ink }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: k.color }} />{k.label}</span></td>
                <td style={{ ...td, fontWeight: 800 }}>{e.number}</td>
                <td style={{ ...td, color: '#374151' }}>{e.name}</td>
                <td style={td} onClick={ev => { if (rigs) ev.stopPropagation(); }}>
                  {rigs ? (
                    // Change the drill right here (Ryan, Oct 31 2026); red when it's double-booked.
                    <select value={e.card.rig || ''} onChange={ev => onSave(e, { rig: ev.target.value || null })}
                      title={clash ? `Double-booked with ${e.card.clashes.map(c => c.job_number).join(', ')}` : 'Change the drill'}
                      style={{ fontSize: 12, fontWeight: 800, borderRadius: 999, padding: '3px 8px', cursor: 'pointer', color: clash ? '#b91c1c' : k.ink, background: clash ? '#fef2f2' : k.soft, border: `1.5px solid ${clash ? '#dc2626' : k.color}` }}>
                      <option value="">— no drill —</option>
                      {[...new Set([...rigs, ...(e.card.rig ? [e.card.rig] : [])])].map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  ) : <EntryTag e={e} boardOnly />}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: running ? 700 : 400 }} onClick={ev => { if (editStart) ev.stopPropagation(); }}>
                  {editStart
                    // Type the start too (Ryan, Nov 1 2026): the length stays, so the finish moves with it.
                    ? <input type="date" value={e.from || ''} onChange={ev => ev.target.value && onSave(e, { start: ev.target.value })}
                        style={{ fontSize: 12.5, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit' }} />
                    : shortDate(e.from)}
                </td>
                {canEdit && (
                  <td style={td} onClick={ev => ev.stopPropagation()}>
                  {/* Filler work (Ryan, Nov 1 2026): a small job marked Dirt / Electrical filler sits in the calendar's
                      filler pile on its days, under the Not-scheduled names, for a free crew to jump on. The Small
                      Jobs board column stays what it is. */}
                    {e.wo && (
                    <select value={e.wo.filler || ''} onClick={ev => ev.stopPropagation()} onChange={ev => onSave(e, { filler: ev.target.value || null })}
                      title="Filler work on the Big Picture — a free crew of this trade can jump on it"
                      style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit',
                        color: e.wo.filler ? (CAL_KIND[e.wo.filler] || k).ink : '#6b7280', background: e.wo.filler ? '#fff' : '#f9fafb', border: `1.5px solid ${e.wo.filler ? (CAL_KIND[e.wo.filler] || k).color : '#e5e7eb'}` }}>
                      <option value="">Set date</option>
                      <option value="dirt">Dirt filler work</option>
                      <option value="elec">Electrical filler work</option>
                    </select>
                  )}
                  </td>
                )}
                <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={ev => { if (editEnd) ev.stopPropagation(); }}>
                  {editEnd
                    // Type the finish (Ryan, Oct 31 2026): saved as working days from the start.
                    ? <input type="date" value={(e.card || e.wo).end || ''} min={e.from} onChange={ev => ev.target.value && onSave(e, { end: ev.target.value })}
                        style={{ fontSize: 12.5, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit' }} />
                    : (e.to && e.to !== e.from ? shortDate(e.to) : '')}
                </td>
                <td style={{ ...td, color: '#6b7280', whiteSpace: 'nowrap' }}>{e.pm}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// A pill of two or three choices — used for the line switch and the Cards controls.
function Segmented({ options, value, onPick, tint }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 999, overflow: 'hidden' }}>
      {options.map(o => (
        <button key={o.k} onClick={() => onPick(o.k)}
          style={{ padding: '6px 15px', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800,
            background: value === o.k ? (o.tint || tint || '#374151') : 'var(--surface)',
            color: value === o.k ? '#fff' : 'var(--text-muted)' }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Kanban card ─────────────────────────────────────────────────────────────
const box = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 4px', fontSize: 10.5,
  width: '100%', minWidth: 0, boxSizing: 'border-box', background: '#fff', color: '#111', fontFamily: 'inherit' };

function JobCard({ job, line, today, busy, draggable = true, onDragStart, onDragEnd, onOpen, onDate, onState, dropSide, onDragOverCard, onDropCard }) {
  const evidence = evidenceFor(job);
  const tone = lineTone(job.plan, line, today, evidence);
  const assumed = (job.plan?.[`${line}_state`] || 'not_ready') === 'ready' && !evidence.working;

  return (
    <div draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}
      onDragOver={onDragOverCard} onDrop={onDropCard}
      style={{ background: '#fff', border: `1px solid ${tone.border}`, borderLeft: `4px solid ${tone.fg}`, borderRadius: 9,
        padding: '9px 10px', cursor: draggable ? 'grab' : 'default', opacity: busy ? 0.5 : 1,
        // Inside the card, so hovering never moves it out from under the pointer.
        boxShadow: dropSide
          ? (dropSide === 'after' ? 'inset 0 -3px 0 #e87722' : 'inset 0 3px 0 #e87722')
          : '0 1px 2px rgba(0,0,0,0.04)' }}>
      <button onClick={onOpen}
        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#111' }}>{job.project_number || '—'}</div>
        <div style={{ fontSize: 11.5, color: '#4b5563', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {job.name}
        </div>
      </button>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>👤 {job.pm_name}</div>

      {tone.word && (
        <div style={{ marginTop: 6, padding: '3px 7px', borderRadius: 5, background: tone.bg, color: tone.fg, fontSize: 10.5, fontWeight: 700 }}>
          {tone.word}
        </div>
      )}
      {/* Said plainly: nobody has confirmed this, the date just went by. */}
      {assumed && job.plan?.[`${line}_date`] && (
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, fontStyle: 'italic' }}>assumed from the date</div>
      )}

      {/* Only the line you're following gets its date boxes — four of them on a card
          this size was more clutter than schedule. The other line still says where it
          stands, because a job is only really ready when both are. */}
      <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {LINES.map(l => {
          const st = COL_BY_KEY[columnFor(job, l.key, today)] || COL_BY_KEY.not_ready;
          const followed = l.key === line;
          return (
            <div key={l.key} style={{ opacity: followed ? 1 : 0.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.3, color: l.tint, textTransform: 'uppercase' }}>{l.label}</span>
                <select value={st.key} draggable={false} onClick={e => e.stopPropagation()}
                  onChange={e => onState(l.key, e.target.value)}
                  title={`Set where the ${l.label.toLowerCase()} stands`}
                  style={{ fontSize: 9.5, fontWeight: 800, padding: '1px 4px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${st.tint}55`, background: st.tint + '1f', color: st.tint, fontFamily: 'inherit' }}>
                  {COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {/* The other line's dates aren't shown, so its start is worth a word. */}
                {!followed && job.plan?.[`${l.key}_date`] && (
                  <span style={{ fontSize: 9.5, color: '#9ca3af', fontWeight: 700 }}>{shortDate(job.plan[`${l.key}_date`])}</span>
                )}
              </div>
              {followed && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 4 }}>
                  <input type="date" value={job.plan?.[`${l.key}_date`] || ''} title={`${l.label} start`}
                    onClick={e => e.stopPropagation()} draggable={false}
                    onChange={e => onDate(`${l.key}_date`, e.target.value)} style={box} />
                  <input type="date" value={job.plan?.[`${l.key}_stop`] || ''} title={`${l.label} finish`}
                    onClick={e => e.stopPropagation()} draggable={false}
                    onChange={e => onDate(`${l.key}_stop`, e.target.value)} style={box} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── The list ────────────────────────────────────────────────────────────────
// Same data, arranged for typing rather than dragging: every job on a row, PM as a
// column you sort by rather than a filter you set, and the two shortcuts that make a
// first pass quick — a month to start in, and a length in weeks.
const td = { padding: '4px 4px', verticalAlign: 'middle' };
const listBox = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 5px', fontSize: 11.5, background: '#fff', color: '#111', fontFamily: 'inherit', width: 108, boxSizing: 'border-box' };
const statePick = { ...listBox, width: 100, cursor: 'pointer', fontWeight: 700 };
const tiny = { ...listBox, width: 54, padding: '4px 2px', cursor: 'pointer' };
const wksBox = { ...listBox, width: 40, padding: '4px 3px' };

function SortHead({ label, sortKey, sort, onSort, tint, width, cellStyle }) {
  const on = sort.key === sortKey;
  return (
    <th style={{ textAlign: 'left', padding: '0 6px 6px', whiteSpace: 'nowrap', width, ...cellStyle }}>
      <button onClick={() => onSort(sortKey)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
          fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
          color: on ? (tint || '#111') : '#9ca3af' }}>
        {label}{on ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
}

// Dirt and Electrical each sit in their own tinted pillow — brown and yellow, Ryan's colours — so
// it is plain which three boxes belong to which (Ryan, Oct 29 2026).
const LINE_BAND = { dirt: { bg: '#f6eee6', line: '#8B5A2B', ink: '#7a4c20' }, elec: { bg: '#fff8dc', line: '#F4C430', ink: '#8a6500' } };
function bandCell(lineKey, col, edge) {
  const b = LINE_BAND[lineKey], w = '2px solid ' + b.line, r = 12;
  return {
    background: b.bg,
    ...(col === 0 ? { borderLeft: w } : null), ...(col === 2 ? { borderRight: w } : null),
    ...(edge === 'top' ? { borderTop: w, paddingTop: 8 } : null), ...(edge === 'bottom' ? { borderBottom: w, paddingBottom: 8 } : null),
    ...(edge === 'top' && col === 0 ? { borderTopLeftRadius: r } : null), ...(edge === 'top' && col === 2 ? { borderTopRightRadius: r } : null),
    ...(edge === 'bottom' && col === 0 ? { borderBottomLeftRadius: r } : null), ...(edge === 'bottom' && col === 2 ? { borderBottomRightRadius: r } : null),
  };
}
const bandGap = <td style={{ width: 14, minWidth: 14, padding: 0 }}><div style={{ width: 14 }} /></td>;

// A date box that reads "–" while it's empty, so an undated line isn't a wall of mm/dd/yyyy
// (Ryan, Oct 30 2026). The real date box lies invisibly over the dash, so a click still opens it.
function DashDate({ value, onChange, style }) {
  if (value) return <input type="date" value={value} style={style} onChange={e => onChange(e.target.value)} />;
  return (
    <span style={{ position: 'relative', display: 'inline-block', ...style, padding: 0, border: '1px solid transparent', background: 'transparent' }}>
      <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontWeight: 700, lineHeight: '24px' }}>–</span>
      <input type="date" value="" onChange={e => onChange(e.target.value)} title="Pick a date"
        onClick={e => { if (typeof e.currentTarget.showPicker === 'function') { try { e.currentTarget.showPicker(); } catch { /* not allowed here */ } } }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }} />
    </span>
  );
}

function JobTable({ rows, today, sort, onSort, busy, onDate, onState, onOpen }) {
  if (!rows.length) return <div style={{ fontSize: 13, color: '#9ca3af', padding: 20 }}>No jobs match that.</div>;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 8px', overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
        <thead>
          <tr>
            <SortHead label="Job" sortKey="job" sort={sort} onSort={onSort} width={240} />
            <SortHead label="PM" sortKey="pm" sort={sort} onSort={onSort} />
            {bandGap}
            <SortHead label="Dirt start" sortKey="dirt_date" sort={sort} onSort={onSort} tint={LINE_BAND.dirt.ink} cellStyle={bandCell('dirt', 0, 'top')} />
            <SortHead label="Dirt finish" sortKey="dirt_stop" sort={sort} onSort={onSort} tint={LINE_BAND.dirt.ink} cellStyle={bandCell('dirt', 1, 'top')} />
            <SortHead label="Dirt status" sortKey="dirt_state" sort={sort} onSort={onSort} tint={LINE_BAND.dirt.ink} cellStyle={bandCell('dirt', 2, 'top')} />
            {bandGap}
            <SortHead label="Elec start" sortKey="elec_date" sort={sort} onSort={onSort} tint={LINE_BAND.elec.ink} cellStyle={bandCell('elec', 0, 'top')} />
            <SortHead label="Elec finish" sortKey="elec_stop" sort={sort} onSort={onSort} tint={LINE_BAND.elec.ink} cellStyle={bandCell('elec', 1, 'top')} />
            <SortHead label="Elec status" sortKey="elec_state" sort={sort} onSort={onSort} tint={LINE_BAND.elec.ink} cellStyle={bandCell('elec', 2, 'top')} />
            {bandGap}
            <SortHead label="On the job" sortKey="crew" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((j, ri) => {
            const ev = evidenceFor(j);
            const edge = ri === rows.length - 1 ? 'bottom' : null;
            const cell = (lineKey, col) => ({ ...td, ...bandCell(lineKey, col, edge) });
            return (
              <tr key={j.id} style={{ opacity: busy === j.id ? 0.5 : 1 }}>
                <td style={td}>
                  <button onClick={() => onOpen(j)}
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#111' }}>{j.project_number || '—'}</span>
                    <span style={{ fontSize: 11.5, color: '#6b7280' }}> · {j.name}</span>
                  </button>
                </td>
                <td style={{ ...td, fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{j.pm_name}</td>
                {LINES.map(l => {
                  // A Complete line greys out (Ryan, Oct 30 2026) — still there to read or reopen.
                  const done = columnFor(j, l.key, today) === 'complete';
                  const dim = done ? { opacity: 0.45 } : null;
                  return (
                  <React.Fragment key={l.key}>
                    {bandGap}
                    <td style={{ ...cell(l.key, 0), ...dim }}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {/* A month is a good enough first answer. The 1st of it goes in,
                            and the real day can replace it when it's known — so it leads. */}
                        <select value="" style={tiny} title="Start in a month — puts the 1st in as a baseline"
                          onChange={e => { if (e.target.value) onDate(j, `${l.key}_date`, e.target.value); }}>
                          <option value="">month…</option>
                          {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                        <DashDate value={j.plan?.[`${l.key}_date`] || ''} style={listBox}
                          onChange={v => onDate(j, `${l.key}_date`, v)} />
                      </div>
                    </td>
                    <td style={{ ...cell(l.key, 1), ...dim }}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {/* Length in weeks is how the work is actually talked about, and
                            it's the finish date said another way — so it leads. */}
                        <input type="number" min="0" step="1" style={wksBox} placeholder={j.plan?.[`${l.key}_date`] ? 'wks' : '–'}
                          disabled={!j.plan?.[`${l.key}_date`]}
                          title={j.plan?.[`${l.key}_date`] ? 'How many weeks — sets the finish date' : 'Put a start date in first'}
                          value={weeksOf(j.plan, l.key)}
                          onChange={e => onDate(j, `${l.key}_days`, e.target.value === '' ? '' : Number(e.target.value) * 7)} />
                        <DashDate value={j.plan?.[`${l.key}_stop`] || ''} style={listBox}
                          onChange={v => onDate(j, `${l.key}_stop`, v)} />
                      </div>
                    </td>
                    <td style={{ ...cell(l.key, 2), ...dim }}>
                      <select value={columnFor(j, l.key, today)} style={statePick}
                        onChange={e => onState(j, l.key, e.target.value)}>
                        {COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </td>
                  </React.Fragment>
                  );
                })}
                {bandGap}
                <td style={{ ...td, fontSize: 11, whiteSpace: 'nowrap' }}>
                  {ev.onClock.length
                    ? <span style={{ color: '#15803d', fontWeight: 700 }}>● {ev.onClock.join(', ')}</span>
                    : ev.hours > 0
                      ? <span style={{ color: '#15803d' }}>{ev.hours}h · {shortDate(ev.lastWorked)}</span>
                      : <span style={{ color: '#d1d5db' }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
